<template>
  <div>
    <b-card class="student-detail-card">
      <div class="student-header">
        <b-button
          variant="outline-secondary"
          class="btn-icon rounded-circle back-btn"
          @click="goBackToList"
        >
          <feather-icon icon="ArrowLeftIcon" size="16" />
        </b-button>
        <div class="title-wrapper">
          <div class="d-flex align-items-center flex-wrap">
            <h4 class="mb-0 mr-1">
              {{ studentName || 'Student Details' }}
            </h4>
            <b-badge variant="light-primary" class="text-uppercase badge-pill">
              GR: {{ grNo || 'N/A' }}
            </b-badge>
          </div>
          <small class="text-muted">
            {{ className || 'Class' }} · {{ subjectName || 'Subject' }}
          </small>
        </div>
      </div>

      <div class="student-meta mt-1">
        <div class="meta-item">
          <span class="label">Father Name</span>
          <span class="value">{{ fatherName || 'N/A' }}</span>
        </div>
        <div class="meta-item">
          <span class="label">Class</span>
          <span class="value">{{ className || 'N/A' }}</span>
        </div>
        <div class="meta-item">
          <span class="label">Subject</span>
          <span class="value">{{ subjectName || 'N/A' }}</span>
        </div>
        <div class="meta-item">
          <span class="label">Section</span>
          <span class="value">{{ sectionName || 'N/A' }}</span>
        </div>
        <div class="meta-item">
          <span class="label">Exam Type</span>
          <span class="value">{{ examTypeName || 'N/A' }}</span>
        </div>
        <div class="meta-item">
          <span class="label">Session</span>
          <span class="value">{{ sessionName || 'N/A' }}</span>
        </div>
        <div class="meta-item">
          <span class="label">Type</span>
          <span class="value text-uppercase">{{ typeLabel || 'N/A' }}</span>
        </div>
      </div>

      <b-alert
        v-if="!classId || !subjectId || !type"
        variant="warning"
        show
        class="mb-0 mt-1"
      >
        Class, subject and type are required to load tabulation data.
      </b-alert>
    </b-card>

    <b-card class="tabulation-card">
      <!-- Due Date and Locked Status Badges -->
      <div v-if="dueDate || isLocked" class="d-flex align-items-center gap-2 mb-3">
        <b-badge v-if="dueDate" variant="light-info" class="due-date-badge">
          <feather-icon icon="CalendarIcon" size="14" class="mr-1" />
          Due Date: {{ formatDate(dueDate) }}
        </b-badge>
        <b-badge v-if="isLocked" variant="danger" class="locked-badge">
          <feather-icon icon="LockIcon" size="14" class="mr-1" />
          Locked
        </b-badge>
      </div>

      <div class="d-flex justify-content-between align-items-center flex-wrap mb-2">
        <div class="d-flex align-items-center gap-2">
          <h5 class="mb-0">
            {{ typeLabel ? `${typeLabel} Records` : 'Marks Entry Data' }}
          </h5>
          <b-badge v-if="studentIds.length > 0" variant="light-info" pill class="student-progress-badge">
            Student {{ currentIndex + 1 }} of {{ studentIds.length }}
          </b-badge>
        </div>
        <div class="d-flex align-items-center gap-2">
          <b-badge variant="light-primary" pill class="records-badge">
            {{ 
              isQuestionsBase 
                ? sortedQuestions.length 
                : (isClassTest 
                    ? sortedClassTestRecords.length 
                    : (sortedCategories && sortedCategories.length > 0 
                        ? sortedCategories.length 
                        : totalRows))
            }} Records
          </b-badge>
          <b-button
            v-if="(rights.add || rights.edit) && !isLocked"
            variant="success"
            @click="saveMarks"
            :disabled="submitting || loading || !tabulationSheetId || (!hasMarksEntered && !isMarksDisabled) || !studentStatus"
            class="save-marks-btn"
            size="sm"
          >
            <b-spinner v-if="submitting" small class="mr-50" />
            <feather-icon v-else icon="SaveIcon" size="16" class="mr-50" />
            {{ getSaveButtonText }}
          </b-button>
        </div>
      </div>

      <!-- No Rights Alert -->
      <b-alert
        v-if="!rights.add && !rights.edit"
        variant="warning"
        show
        class="mb-3"
      >
        <feather-icon icon="AlertCircleIcon" size="18" class="mr-1" />
        You do not have permission to save marks. Please contact your administrator.
      </b-alert>

      <!-- Status Selection - Button Style -->
      <div class="status-section mb-3">
        <div class="status-section-content">
          <div class="status-left">
            <div class="d-flex align-items-center mb-2">
              <feather-icon icon="InfoIcon" size="18" class="mr-1 text-primary" />
              <strong class="status-label">Status:</strong>
              <span class="text-muted ml-2">Required to save marks</span>
            </div>
            <div class="status-buttons">
              <b-button
                v-for="option in statusOptions"
                :key="option.value"
                size="md"
                :class="['status-btn', `status-btn-${option.value.toLowerCase().replace('/', '-')}`, { 'status-btn-active': studentStatus === option.value }]"
                @click="studentStatus = option.value"
                :disabled="isLocked"
              >
                {{ option.value }}
              </b-button>
            </div>
          </div>
          
          <!-- Icon Reference Section - Only for Icon Base Type, on the right side -->
          <div v-if="type === 'icon-base'" class="status-right icon-reference-wrapper">
            <div class="icon-reference-list">
              <div class="icon-reference-item">
                <img 
                  src="https://cdn.cloudious.net/file-1767856379540-374108345.jpg" 
                  alt="Excellent" 
                  class="reference-icon"
                />
                <div class="reference-text">
                  <span class="reference-label">Excellent</span>
                  <span class="reference-description">The child Knows it very well.</span>
                </div>
              </div>
              <div class="icon-reference-item">
                <img 
                  src="https://cdn.cloudious.net/file-1767856379583-832802350.jpg" 
                  alt="Good" 
                  class="reference-icon"
                />
                <div class="reference-text">
                  <span class="reference-label">Good</span>
                  <span class="reference-description">The child is almost getting it.</span>
                </div>
              </div>
              <div class="icon-reference-item">
                <img 
                  src="https://cdn.cloudious.net/file-1767856379583-811045679.jpg" 
                  alt="Needs Improvement" 
                  class="reference-icon"
                />
                <div class="reference-text">
                  <span class="reference-label">Needs Improvement</span>
                  <span class="reference-description">The child will accomplish it soon with the help of the teacher and parents / caregiver.</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Questions Base UI -->
      <div v-if="isQuestionsBase">
        <!-- Loading State -->
        <div v-if="loading" class="text-center py-5">
          <b-spinner variant="primary" style="width: 3rem; height: 3rem" />
          <p class="mt-2 text-muted">Loading records...</p>
        </div>

        <!-- Empty State for Questions Base -->
        <div v-else-if="sortedQuestions.length === 0" class="empty-state-box">
          <div class="empty-state-content">
            <div class="empty-state-icon">
              <feather-icon icon="FileTextIcon" size="64" />
            </div>
            <h4 class="empty-state-title">No Questions Base Records</h4>
            <p class="empty-state-message">
              There is no data saved for the selected filters.
            </p>
            <p class="empty-state-submessage">
              Please check your filters or contact the administrator.
            </p>
          </div>
        </div>

        <!-- Questions Base Content -->
        <div v-else class="questions-wrapper">
        <!-- Total Marks Summary at Top -->
        <div class="marks-summary-card">
          <div class="summary-header">
            <feather-icon icon="CalculatorIcon" size="18" class="mr-1" />
            <span>Total Marks</span>
          </div>
          <div class="summary-content">
            <div class="summary-item">
              <span class="summary-label">Entered</span>
              <span class="summary-value entered">{{ totalMarksEntered }}</span>
            </div>
            <div class="summary-divider"></div>
            <div class="summary-item">
              <span class="summary-label">Out of</span>
              <span class="summary-value total">{{ totalMarksAvailable }}</span>
            </div>
            <div class="summary-percentage">
              <div class="percentage-bar">
                <div
                  class="percentage-fill"
                  :style="{ width: `${marksPercentage}%` }"
                ></div>
              </div>
              <span class="percentage-text">{{ marksPercentage }}%</span>
            </div>
          </div>
        </div>

        <div class="questions-header">
          <div class="questions-title">
            <feather-icon icon="FileTextIcon" size="20" class="mr-1" />
            <span>Questions</span>
          </div>
          <b-badge variant="light-primary" pill class="questions-count">
            {{ sortedQuestions.length }} Questions
          </b-badge>
        </div>

        <div class="questions-list">
          <div
            v-for="(question, index) in sortedQuestions"
            :key="question.id"
            class="question-card"
            :style="{ animationDelay: `${index * 0.1}s` }"
          >
            <div class="question-header">
              <div class="question-number-badge">
                <span class="question-number">{{ index + 1 }}</span>
              </div>
              <div class="question-info">
                <div class="question-title">
                  {{ question.questionNumber || question.name || question.questionName || question.title || `Question ${index + 1}` }}
                </div>
                <div class="question-meta">
                  <b-badge variant="light-info" class="marks-badge">
                    Max: {{ question.totalMarks || 0 }} marks
                  </b-badge>
                </div>
              </div>
            </div>

            <div class="marks-input-section">
              <label class="marks-label">Enter Marks</label>
              <div class="marks-input-wrapper">
                <b-form-input
                  v-model.number="questionMarks[question.id]"
                  type="number"
                  :min="0"
                  :max="question.totalMarks || 0"
                  step="0.1"
                  :placeholder="`0`"
                  class="marks-input"
                  :disabled="isLocked || isMarksDisabled"
                  @input="(e) => handleMarksInput(e, question.id, question.totalMarks)"
                  @blur="validateMarks(question.id, question.totalMarks)"
                  @keyup="validateMarks(question.id, question.totalMarks)"
                  @keydown="(e) => preventInvalidInput(e, question.totalMarks || 0)"
                  @paste="(e) => handlePaste(e, question.totalMarks || 0)"
                />
                <span class="marks-slash">/</span>
                <span class="marks-total">{{ question.totalMarks || 0 }}</span>
              </div>
            </div>
          </div>
        </div>
        </div>
      </div>

      <!-- Icon Base UI -->
      <div v-else-if="!isQuestionsBase && type === 'icon-base'">
        <!-- Loading State -->
        <div v-if="loading" class="text-center py-5">
          <b-spinner variant="primary" style="width: 3rem; height: 3rem" />
          <p class="mt-2 text-muted">Loading records...</p>
        </div>

        <!-- Empty State for Icon Base -->
        <div v-else-if="sortedCategories.length === 0" class="empty-state-box">
          <div class="empty-state-content">
            <div class="empty-state-icon">
              <feather-icon icon="FolderIcon" size="64" />
            </div>
            <h4 class="empty-state-title">No Icon Base Records</h4>
            <p class="empty-state-message">
              There is no data saved for the selected filters.
            </p>
            <p class="empty-state-submessage">
              Please check your filters or contact the administrator.
            </p>
          </div>
        </div>

        <!-- Icon Base Content -->
        <div v-else class="categories-wrapper">
          <!-- Dual-language boxed grid (myskool / myskoolprofectus + bothRemarks) -->
          <div
            v-if="hasDualLanguageGridLayout"
            class="dual-lang-grid-scroll"
          >
            <div class="dual-lang-grid">
              <div
                v-for="(category, catIndex) in dualLanguageGridCategories"
                :key="`dual-grid-${category.id}`"
                class="dual-lang-column"
                :style="{ animationDelay: `${catIndex * 0.08}s` }"
              >
                <div class="dual-lang-row dual-lang-header-row">
                  <div class="dual-lang-cell dual-lang-category-cell">
                    {{ category.categoryName || 'Category' }}
                  </div>
                  <div class="dual-lang-cell dual-lang-lang-head">E</div>
                  <div class="dual-lang-cell dual-lang-lang-head">U</div>
                </div>

                <div
                  v-for="(option, optIndex) in category.options || []"
                  :key="option.id || optIndex"
                  class="dual-lang-row"
                >
                  <div class="dual-lang-cell dual-lang-text-cell">
                    {{ option.optionName }}
                  </div>
                  <button
                    type="button"
                    class="dual-lang-cell dual-lang-mark-cell"
                    :class="getMarkCellClass(category.id, option.id, 'E')"
                    :disabled="isLocked || isMarksDisabled"
                    @click="cycleLanguageRating(category.id, option.id, 'E')"
                  >
                    <span
                      v-if="hasLanguageRating(category.id, option.id, 'E')"
                      class="mark-display"
                    >
                      X
                    </span>
                    <span
                      v-if="hasLanguageRating(category.id, option.id, 'E')"
                      class="mark-clear-x"
                      @click.stop="
                        clearLanguageRating(category.id, option.id, 'E')
                      "
                    >
                      ×
                    </span>
                  </button>
                  <button
                    type="button"
                    class="dual-lang-cell dual-lang-mark-cell"
                    :class="getMarkCellClass(category.id, option.id, 'U')"
                    :disabled="isLocked || isMarksDisabled"
                    @click="cycleLanguageRating(category.id, option.id, 'U')"
                  >
                    <span
                      v-if="hasLanguageRating(category.id, option.id, 'U')"
                      class="mark-display"
                    >
                      X
                    </span>
                    <span
                      v-if="hasLanguageRating(category.id, option.id, 'U')"
                      class="mark-clear-x"
                      @click.stop="
                        clearLanguageRating(category.id, option.id, 'U')
                      "
                    >
                      ×
                    </span>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Standard icon-base cards (other domains or non-bothRemarks) -->
          <div
            v-for="(category, catIndex) in standardIconCategories"
            :key="category.id"
            class="category-card"
            :style="{ animationDelay: `${catIndex * 0.1}s` }"
          >
          <div class="category-header">
            <div class="icon-wrapper">
              <feather-icon icon="FolderIcon" size="18" />
            </div>
            <div class="title-block">
              <h6 class="mb-0 text-uppercase">
                {{ category.categoryName || 'Category' }}
              </h6>
              <small class="text-muted">Sort {{ category.sortIndex || '-' }}</small>
            </div>
            <b-badge variant="light-primary" pill>
              {{ (category.options || []).length }} Options
            </b-badge>
          </div>

          <!-- Select All Section -->
          <div v-if="category.options && category.options.length > 0" class="select-all-section">
            <span class="select-all-label">Select All:</span>
            <div class="select-all-buttons">
              <b-button
                v-for="rating in ratingOptions"
                :key="`select-all-${rating.value}`"
                size="sm"
                variant="outline-primary"
                :class="['select-all-btn', `select-all-${rating.value}`]"
                @click="selectAllRatingsForCategory(category.id, rating.value)"
                :disabled="isLocked || isMarksDisabled"
              >
                <span class="emoji">{{ rating.emoji }}</span>
                <span class="label">{{ rating.label }}</span>
              </b-button>
            </div>
          </div>

          <div class="category-divider" />

          <div v-if="category.options && category.options.length" class="options-list">
            <div
              v-for="(option, optIndex) in category.options"
              :key="option.id"
              class="option-row"
              :style="{ animationDelay: `${(catIndex * 0.1) + (optIndex * 0.05)}s` }"
            >
              <div class="option-label">
                <span class="chip-index">{{ option.sortIndex }}</span>
                <span class="chip-label">{{ option.optionName }}</span>
              </div>
              <div
                v-if="showDualLanguageForCategory(category, option)"
                class="option-lang-pills"
              >
                <button
                  type="button"
                  class="option-lang-pill"
                  :class="{
                    active: getActiveLanguage(category.id, option.id) === 'E',
                  }"
                  :disabled="isLocked || isMarksDisabled"
                  @click="handleLanguageSelect(category.id, option.id, 'E')"
                >
                  <span class="option-lang-code">E</span>
                  <feather-icon
                    v-if="
                      getActiveLanguage(category.id, option.id) === 'E' &&
                      hasLanguageRating(category.id, option.id, 'E')
                    "
                    icon="XIcon"
                    size="12"
                    class="lang-clear-x"
                    @click.stop="clearLanguageRating(category.id, option.id, 'E')"
                  />
                </button>
                <button
                  type="button"
                  class="option-lang-pill"
                  :class="{
                    active: getActiveLanguage(category.id, option.id) === 'U',
                  }"
                  :disabled="isLocked || isMarksDisabled"
                  @click="handleLanguageSelect(category.id, option.id, 'U')"
                >
                  <span class="option-lang-code">U</span>
                  <feather-icon
                    v-if="
                      getActiveLanguage(category.id, option.id) === 'U' &&
                      hasLanguageRating(category.id, option.id, 'U')
                    "
                    icon="XIcon"
                    size="12"
                    class="lang-clear-x"
                    @click.stop="clearLanguageRating(category.id, option.id, 'U')"
                  />
                </button>
              </div>
              <div class="rating-pills">
                <b-button
                  v-for="rating in ratingOptions"
                  :key="rating.value"
                  size="sm"
                  :variant="
                    isRatingSelected(category.id, option.id, rating.value)
                      ? 'gradient-primary'
                      : 'outline-primary'
                  "
                  :class="[
                    'rating-pill',
                    {
                      'rating-selected': isRatingSelected(category.id, option.id, rating.value),
                      [`rating-${rating.value}`]: true
                    }
                  ]"
                  @click="setOptionRating(category.id, option.id, rating.value)"
                  :disabled="isLocked || isMarksDisabled"
                >
                  <span class="emoji">{{ rating.emoji }}</span>
                  <span class="label">{{ rating.label }}</span>
                </b-button>
              </div>
            </div>
          </div>
          <div v-else class="text-muted small">No options found</div>
        </div>
        </div>
      </div>

      <!-- Class Test Records UI -->
      <div v-else-if="!isQuestionsBase && type === 'class-test'">
        <!-- Loading State -->
        <div v-if="loading" class="text-center py-5">
          <b-spinner variant="primary" style="width: 3rem; height: 3rem" />
          <p class="mt-2 text-muted">Loading records...</p>
        </div>

        <!-- Class Test Cards -->
        <div v-else-if="sortedClassTestRecords.length > 0" class="class-test-wrapper">
          <div class="class-test-list">
            <div
              v-for="(record, index) in sortedClassTestRecords"
              :key="record.id || index"
              class="class-test-card"
              :style="{ animationDelay: `${index * 0.1}s` }"
            >
              <div class="class-test-header">
                <div class="test-number-badge">
                  <span class="test-number">{{ record.sortIndex || index + 1 }}</span>
                </div>
                <div class="test-title-section">
                  <h6 class="test-name">{{ record.name || record.testName || `Test ${index + 1}` }}</h6>
                  <div class="test-meta">
                    <span v-if="record.questionNumber" class="test-meta-item">
                      <feather-icon icon="HashIcon" size="14" />
                      Q{{ record.questionNumber }}
                    </span>
                    <span class="test-meta-item">
                      <feather-icon icon="ListIcon" size="14" />
                      {{ record.totalQuestion || record.totalQuestions || 0 }} Questions
                    </span>
                  </div>
                </div>
              </div>
              
              <div class="class-test-content">
                <div class="marks-display">
                  <div class="marks-input-section">
                    <label class="marks-label">Enter Marks</label>
                    <div class="marks-input-wrapper">
                      <b-form-input
                        v-model.number="classTestMarks[record.id]"
                        :disabled="isLocked || isMarksDisabled"
                        type="number"
                        :min="0"
                        :max="record.totalMarks || record.totalQuestion || 0"
                        step="0.1"
                        :placeholder="`0`"
                        class="marks-input"
                        @input="(e) => handleClassTestMarksInput(e, record.id, record.totalMarks || record.totalQuestion || 0)"
                        @blur="validateClassTestMarks(record.id, record.totalMarks || record.totalQuestion || 0)"
                        @keyup="validateClassTestMarks(record.id, record.totalMarks || record.totalQuestion || 0)"
                        @keydown="(e) => preventInvalidInput(e, record.totalMarks || record.totalQuestion || 0)"
                        @paste="(e) => handlePaste(e, record.totalMarks || record.totalQuestion || 0)"
                      />
                      <span class="marks-slash">/</span>
                      <span class="marks-total">{{ record.totalMarks || record.totalQuestion || 0 }}</span>
                    </div>
                  </div>
                  <div class="marks-progress">
                    <div class="progress-bar">
                      <div
                        class="progress-fill"
                        :style="{
                          width: `${getClassTestMarksPercentage(record.id, record.totalMarks || record.totalQuestion || 0)}%`
                        }"
                      ></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Empty State -->
        <div v-else class="empty-state-box">
          <div class="empty-state-content">
            <div class="empty-state-icon">
              <feather-icon icon="FileTextIcon" size="64" />
            </div>
            <h4 class="empty-state-title">No Class Test Records</h4>
            <p class="empty-state-message">
              There is no data saved for the selected filters.
            </p>
            <p class="empty-state-submessage">
              Please check your filters or contact the administrator.
            </p>
          </div>
        </div>
      </div>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BButton,
  BBadge,
  BAlert,
  BSpinner,
  BFormInput,
} from 'bootstrap-vue'
import { mapActions } from 'vuex'

export default {
  name: 'MarksEntryStudent',
  components: {
    BCard,
    BButton,
    BBadge,
    BAlert,
    BSpinner,
    BFormInput,
  },
      data() {
    return {
      rights: {},
      loading: false,
      submitting: false,
      tabulationSheetId: null, // Store the tabulation sheet ID
      isLocked: false, // Lock status from tabulation sheet
      dueDate: null, // Due date from tabulation sheet
      records: [],
      totalRows: 0,
      categories: [],
      optionRatings: {},
      optionUrduRatings: {},
      optionLanguageMode: {},
      questionMarks: {}, // Store marks for each question
      classTestMarks: {}, // Store marks for each class test
      existingMarksData: null, // Store existing marks data if found
      detailRecordIds: {}, // Store detail record IDs: { questionID: detailId } or { 'categoryID-optionID': detailId }
      studentStatus: 'P', // Default status: P (Present), options: P, A, L, N/A
      allStudents: [], // Store all students for the class/section
      allStudentsMarks: {}, // Store marks for all students: { studentId: { marksData, status } }
      statusOptions: [
        { value: 'P', text: 'P' },
        { value: 'A', text: 'A' },
        { value: 'L', text: 'L' },
        { value: 'N/A', text: 'N/A' },
      ],
      ratingOptions: [
        { label: 'Excellent', value: 'excellent', emoji: '🌟' },
        { label: 'Good', value: 'good', emoji: '👍' },
        { label: 'Needs Improvement', value: 'bad', emoji: '⚠️' },
      ],
      defaultFields: [
        { key: 'studentName', label: 'Student' },
        { key: 'grNo', label: 'GR No' },
        { key: 'className', label: 'Class' },
        { key: 'sectionName', label: 'Section' },
        { key: 'subjectName', label: 'Subject' },
        { key: 'examTypeName', label: 'Exam Type' },
        { key: 'sessionName', label: 'Session' },
        { key: 'type', label: 'Type' },
      ],
    }
  },
  computed: {
    studentId() {
      return this.$route.query.studentId
    },
    studentName() {
      return this.$route.query.studentName
    },
    grNo() {
      return this.$route.query.grNo
    },
    fatherName() {
      return this.$route.query.fatherName
    },
    classId() {
      return Number(this.$route.query.classId) || null
    },
    className() {
      return this.$route.query.className
    },
    subjectId() {
      return Number(this.$route.query.subjectId) || null
    },
    subjectName() {
      return this.$route.query.subjectName
    },
    sectionId() {
      return Number(this.$route.query.sectionId) || 0
    },
    sectionName() {
      return this.$route.query.sectionName
    },
    examTypeId() {
      return Number(this.$route.query.examTypeId) || null
    },
    examTypeName() {
      return this.$route.query.examTypeName
    },
    sessionId() {
      return Number(this.$route.query.sessionId) || null
    },
    sessionName() {
      return this.$route.query.sessionName
    },
    type() {
      return this.$route.query.type
    },
    typeLabel() {
      return this.$route.query.typeLabel || this.formatLabel(this.type)
    },
    reportDomain() {
      return (this.$store.state.report || '').replace(/_/g, '')
    },
    isRemarksBaseSchool() {
      return this.reportDomain === 'myskool' || this.reportDomain === 'myskoolprofectus'
    },
    marksRecordId() {
      return this.$route.query.marksRecordId ? Number(this.$route.query.marksRecordId) : null
    },
    hasExistingMarks() {
      return this.$route.query.hasExistingMarks === 'true'
    },
    tID() {
      return this.$route.query.tID ? Number(this.$route.query.tID) : null
    },
    studentIds() {
      // Get the student IDs list from query (comma-separated string)
      return this.$route.query.studentIds ? this.$route.query.studentIds.split(',').map(id => Number(id)) : []
    },
    currentIndex() {
      // Handle currentIndex correctly, including when it's 0 (first student)
      const index = this.$route.query.currentIndex
      if (index !== null && index !== undefined && index !== '') {
        const parsed = Number(index)
        if (!isNaN(parsed) && parsed >= 0) {
          return parsed
        }
      }
      // Fallback: try to find the current student in the studentIds array
      if (this.studentId && this.studentIds.length > 0) {
        const foundIndex = this.studentIds.findIndex(id => id === Number(this.studentId))
        return foundIndex >= 0 ? foundIndex : -1
      }
      return -1
    },
    hasNext() {
      return this.studentIds.length > 0 && this.currentIndex >= 0 && this.currentIndex < this.studentIds.length - 1
    },
    nextStudentId() {
      if (this.hasNext) {
        return this.studentIds[this.currentIndex + 1]
      }
      return null
    },
    getSaveButtonText() {
      if (this.submitting) {
        return 'Saving...'
      }
      // Check if there are more students after current one
      if (this.studentIds.length > 0 && this.currentIndex >= 0 && this.currentIndex < this.studentIds.length - 1) {
        return 'Save & Next'
      }
      return 'Save'
    },
    isQuestionsBase() {
      return this.type === 'questions-base'
    },
    isClassTest() {
      return this.type === 'class-test'
    },
    sortedCategories() {
      if (!this.categories || !Array.isArray(this.categories)) return []
      return this.categories
        .map((cat) => ({
          ...cat,
          options: (cat.options || [])
            .filter((opt) => !opt.deleted)
            .sort((a, b) => (a.sortIndex || 0) - (b.sortIndex || 0)),
        }))
        .filter((cat) => !cat.deleted)
        .sort((a, b) => (a.sortIndex || 0) - (b.sortIndex || 0))
    },
    hasDualLanguageGridLayout() {
      if (!this.isRemarksBaseSchool || this.type !== 'icon-base') return false
      return this.sortedCategories.some((cat) =>
        this.showDualLanguageForCategory(cat)
      )
    },
    dualLanguageGridCategories() {
      if (!this.hasDualLanguageGridLayout) return []
      return this.sortedCategories.filter((cat) =>
        this.showDualLanguageForCategory(cat)
      )
    },
    standardIconCategories() {
      if (!this.hasDualLanguageGridLayout) return this.sortedCategories
      return this.sortedCategories.filter(
        (cat) => !this.showDualLanguageForCategory(cat)
      )
    },
    sortedQuestions() {
      if (!this.records || !Array.isArray(this.records)) return []
      return this.records
        .filter((q) => !q.deleted)
        .sort((a, b) => (a.sortIndex || 0) - (b.sortIndex || 0))
    },
    sortedClassTestRecords() {
      if (!this.records || !Array.isArray(this.records)) return []
      return this.records
        .filter((r) => !r.deleted)
        .sort((a, b) => (a.sortIndex || 0) - (b.sortIndex || 0))
    },
    totalMarksAvailable() {
      return this.sortedQuestions.reduce((sum, q) => {
        return sum + (Number(q.totalMarks) || 0)
      }, 0)
    },
    totalMarksEntered() {
      const total = this.sortedQuestions.reduce((sum, q) => {
        const marks = Number(this.questionMarks[q.id]) || 0
        // Round each mark to 2 decimal places before summing to avoid floating point errors
        const roundedMarks = Math.round(marks * 100) / 100
        return sum + roundedMarks
      }, 0)
      // Round the final total to 2 decimal places to handle any floating point precision issues
      return Math.round(total * 100) / 100
    },
    marksPercentage() {
      if (this.totalMarksAvailable === 0) return 0
      return Math.round((this.totalMarksEntered / this.totalMarksAvailable) * 100)
    },
    isMarksDisabled() {
      // Disable marks entry when status is Absent (A), Leave (L), or N/A
      return this.studentStatus === 'A' || this.studentStatus === 'L' || this.studentStatus === 'N/A'
    },
    hasMarksEntered() {
      // Allow saving if status is Absent, Leave, or N/A (even without marks)
      if (this.isMarksDisabled) {
        return true
      }
      
      if (this.isQuestionsBase) {
        return this.sortedQuestions.some((q) => {
          const val = this.questionMarks[q.id]
          return val !== null && val !== undefined && val !== ''
        })
      } else if (this.type === 'icon-base') {
        return (
          Object.keys(this.optionRatings).length > 0 ||
          Object.keys(this.optionUrduRatings).length > 0
        )
      } else if (this.type === 'class-test') {
        return this.sortedClassTestRecords.some((test) => {
          const val = this.classTestMarks[test.id]
          return val !== null && val !== undefined && val !== ''
        })
      }
      return false
    },
    tableFields() {
      if (!this.records.length) return this.defaultFields
      const sample = this.records[0]
      const fields = []

      this.defaultFields.forEach((field) => {
        if (sample[field.key] !== undefined) fields.push(field)
      })

      const includedKeys = new Set(fields.map((f) => f.key))
      Object.keys(sample).forEach((key) => {
        if (!includedKeys.has(key)) {
          fields.push({
            key,
            label: this.formatLabel(key),
          })
        }
      })
      return fields
    },
  },
  async created() {
    // Initialize rights from menu and enforce view permission
    let right = {};
    this.$store.state.menu.forEach((el) => {
      el.children.forEach((ch) => {
        right[ch.route] = {
          view: ch.view,
          add: ch.add,
          edit: ch.edit,
          delete: ch.delete,
        };
      });
    });
    this.$store.commit("setRights", right);

    // Use the same route name as marksEntrySimple for rights checking
    const routeName = 'marks-entry-staff';
    console.log('Marks Entry Student - Using route name for rights:', routeName);
    console.log('Available rights:', this.$store.state.rights);
    
    if (!this.$store.state.rights[routeName]) {
      console.log('No rights found for route:', routeName);
      this.$router.replace({ name: "misc-not-authorized" });
      return;
    }
    this.rights = this.$store.state.rights[routeName];
    console.log('User rights for marks entry student:', this.rights);
    
    if (!this.rights.view) {
      console.log('No view permission');
      this.$router.replace({ name: "misc-not-authorized" });
      return;
    }

    // Debug logging for navigation
    console.log('Route query params:', this.$route.query)
    console.log('Student IDs:', this.studentIds)
    console.log('Current Index (raw query):', this.$route.query.currentIndex)
    console.log('Current Index (computed):', this.currentIndex)
    console.log('Current Index + 1 (display):', this.currentIndex + 1)
    console.log('Total Students:', this.studentIds.length)
    console.log('Has Next:', this.hasNext)
    console.log('Next Student ID:', this.nextStudentId)
    console.log('Save Button Text:', this.getSaveButtonText)
    
    if (this.classId && this.subjectId && this.type) {
      await this.loadStudentData()
    }
  },
  watch: {
    // Watch for studentId changes to reload data when navigating to next student
    '$route.query.studentId': {
      handler(newStudentId, oldStudentId) {
        // Only reload if studentId actually changed and we have required data
        if (newStudentId && oldStudentId && newStudentId !== oldStudentId && this.classId && this.subjectId && this.type) {
          console.log('Student ID changed, reloading data...', { newStudentId, oldStudentId })
          this.loadStudentData()
        }
      },
      immediate: false, // Don't trigger on initial load
    },
    // Watch for status changes to clear marks when status is A, L, or N/A
    studentStatus(newStatus, oldStatus) {
      // Only clear if status changed to A, L, or N/A (and wasn't already one of these)
      if (oldStatus && newStatus && oldStatus !== newStatus) {
        if (newStatus === 'A' || newStatus === 'L' || newStatus === 'N/A') {
          // Clear all marks when status changes to Absent, Leave, or N/A
          this.questionMarks = {}
          this.classTestMarks = {}
          this.optionRatings = {}
          console.log('Status changed to', newStatus, '- cleared all marks')
        }
      }
    },
  },
  methods: {
    formatDate(dateString) {
      if (!dateString) return 'N/A'
      try {
        const date = new Date(dateString)
        return date.toLocaleDateString('en-US', { 
          year: 'numeric', 
          month: 'short', 
          day: 'numeric' 
        })
      } catch (e) {
        return dateString
      }
    },
    async loadStudentData() {
      // Clear previous student's data first
      this.clearStudentData()
      
      // Show loading state
      this.loading = true
      
      try {
        console.log('loadStudentData: Starting data load for student:', this.studentId)
        
        // Step 1: Load tabulation sheet structure (questions/records)
        console.log('loadStudentData: Step 1 - Loading tabulation data...')
        await this.loadTabulationData()
        console.log('loadStudentData: Step 1 complete - Tabulation data loaded')
        console.log('questionMarks after loadTabulationData:', JSON.parse(JSON.stringify(this.questionMarks)))
        
        // Step 2: Load all students for the class/section
        console.log('loadStudentData: Step 2 - Loading all students...')
        await this.loadAllStudents()
        console.log('loadStudentData: Step 2 complete - All students loaded')
        
        // Step 3: Load existing marks (only for the current student)
        console.log('loadStudentData: Step 3 - Loading existing marks...')
        await this.loadExistingMarks()
        console.log('loadStudentData: Step 3 complete - Existing marks loaded')
        console.log('questionMarks after loadExistingMarks:', JSON.parse(JSON.stringify(this.questionMarks)))
      } catch (error) {
        console.error('Error loading student data:', error)
        this.$bvToast.toast('Error loading student data', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        })
      } finally {
        this.loading = false
        console.log('loadStudentData: Complete - Final questionMarks:', JSON.parse(JSON.stringify(this.questionMarks)))
      }
    },
    clearStudentData() {
      // Clear all marks data for previous student
      this.questionMarks = {}
      this.classTestMarks = {}
      this.optionRatings = {}
      this.existingMarksData = null
      this.marksRecordId = null
      this.detailRecordIds = {} // Clear detail record IDs
      this.studentStatus = 'P' // Reset to default
      this.tabulationSheetId = null
      this.isLocked = false
      this.dueDate = null
      this.records = []
      this.categories = []
      this.totalRows = 0
    },
    goBackToList() {
      // Navigate directly to marks entry list page instead of going back
      this.$router.push({
        name: 'marks-entry-staff',
        query: {
          // Preserve any filter parameters if needed
        }
      })
    },
    ...mapActions(['get', 'post']),
    async loadTabulationData() {
      if (!this.classId || !this.subjectId || !this.type) return

      this.loading = true

      try {
        const params = new URLSearchParams({
          db: this.$store.state.userData.db,
          cID: this.$store.state.userData.cId,
          classId: this.classId,
          sectionId: 0,
          subjectId: this.subjectId,
          type: this.type,
        })
        if (this.examTypeId) params.append('examTypeId', this.examTypeId)
        if (this.sessionId) params.append('sessionId', this.sessionId)

        const url = `${this.$store.state.domain}TabulationSheet/LoadTabulationData?${params.toString()}`
        const response = await this.get({
          url,
          token: this.$store.state.userData.token,
        })

        let rows = []
        let categories = []
        
        // Handle different response structures
        console.log('API Response:', response)
        
        if (response) {
          // Try multiple response structures
          let dataObj = null
          
          // Check for nested data structure: response.data.data
          // Based on API response: { data: { id, isLocked, dueDate, records, ... }, status: "success" }
          if (response.data && response.data.data) {
            dataObj = response.data.data
          } else if (response.data && typeof response.data === 'object' && !Array.isArray(response.data)) {
            // response.data is the object itself (not an array)
            dataObj = response.data
          } else if (response.data) {
            dataObj = response.data
          } else {
            dataObj = response
          }
          
          // Extract tabulation sheet ID from response
          if (dataObj && dataObj.id) {
            this.tabulationSheetId = dataObj.id
            console.log('Extracted tabulation sheet ID from dataObj:', this.tabulationSheetId)
          } else if (response.data && response.data.id) {
            this.tabulationSheetId = response.data.id
            console.log('Extracted tabulation sheet ID from response.data:', this.tabulationSheetId)
          } else if (response.id) {
            this.tabulationSheetId = response.id
            console.log('Extracted tabulation sheet ID from response:', this.tabulationSheetId)
          }
          
          // Extract isLocked and dueDate from response
          // API response structure: { data: { id, isLocked, dueDate, records, ... }, status: "success" }
          console.log('Full response structure:', JSON.stringify(response, null, 2))
          console.log('response.data:', response.data)
          console.log('dataObj:', dataObj)
          
          // Try multiple paths to extract isLocked and dueDate
          let extractedIsLocked = false
          let extractedDueDate = null
          
          if (response.data && response.data.data && (response.data.data.isLocked !== undefined || response.data.data.dueDate)) {
            extractedIsLocked = response.data.data.isLocked === true || response.data.data.isLocked === 1 || response.data.data.isLocked === 'true'
            extractedDueDate = response.data.data.dueDate || null
            console.log('Extracted from response.data.data:', extractedIsLocked, extractedDueDate)
          } else if (dataObj && (dataObj.isLocked !== undefined || dataObj.dueDate)) {
            extractedIsLocked = dataObj.isLocked === true || dataObj.isLocked === 1 || dataObj.isLocked === 'true'
            extractedDueDate = dataObj.dueDate || null
            console.log('Extracted from dataObj:', extractedIsLocked, extractedDueDate)
          } else if (response.data && (response.data.isLocked !== undefined || response.data.dueDate)) {
            extractedIsLocked = response.data.isLocked === true || response.data.isLocked === 1 || response.data.isLocked === 'true'
            extractedDueDate = response.data.dueDate || null
            console.log('Extracted from response.data:', extractedIsLocked, extractedDueDate)
          } else if (response.isLocked !== undefined || response.dueDate) {
            extractedIsLocked = response.isLocked === true || response.isLocked === 1 || response.isLocked === 'true'
            extractedDueDate = response.dueDate || null
            console.log('Extracted from response:', extractedIsLocked, extractedDueDate)
          }
          
          // Use $set to ensure Vue reactivity
          this.$set(this, 'isLocked', extractedIsLocked)
          this.$set(this, 'dueDate', extractedDueDate)
          
          console.log('Final isLocked:', this.isLocked, 'Final dueDate:', this.dueDate)
          
          // Extract categories - check multiple possible locations
          if (Array.isArray(dataObj.categories)) {
            categories = dataObj.categories
          } else if (Array.isArray(response.data?.categories)) {
            categories = response.data.categories
          } else if (Array.isArray(response.categories)) {
            categories = response.categories
          }
          
          // Extract records
          if (Array.isArray(dataObj.records)) {
            rows = dataObj.records
          } else if (Array.isArray(response.data?.records)) {
            rows = response.data.records
          } else if (Array.isArray(response.records)) {
            rows = response.records
          } else if (Array.isArray(response.data) && !response.data.data) {
            rows = response.data
          } else if (Array.isArray(response)) {
            rows = response
          }
        }

        this.records = rows || []
        this.categories = categories || []
        this.optionRatings = {}
        this.optionUrduRatings = {}
        this.optionLanguageMode = {}
        this.questionMarks = {} // Reset marks when loading new data
        this.classTestMarks = {} // Reset class test marks when loading new data
        
        console.log('loadTabulationData: Initialized empty marks objects')
        
        // Initialize marks with 0 for all questions/tests
        if (rows && Array.isArray(rows)) {
          console.log(`loadTabulationData: Initializing ${rows.length} records with 0 marks`)
          rows.forEach((record) => {
            if (record.id) {
              // Initialize with 0 by default
              if (this.type === 'questions-base') {
                this.$set(this.questionMarks, record.id, 0)
                console.log(`loadTabulationData: Initialized questionMarks[${record.id}] = 0`)
              } else if (this.type === 'class-test') {
                this.$set(this.classTestMarks, record.id, 0)
                console.log(`loadTabulationData: Initialized classTestMarks[${record.id}] = 0`)
              }
            }
          })
        }
        
        this.totalRows = categories.length || rows.length || 0
        
        // Debug log
        console.log('Parsed categories:', this.categories)
        console.log('Categories count:', this.categories.length)
        console.log('Parsed records:', this.records)
        console.log('questionMarks after loadTabulationData:', JSON.parse(JSON.stringify(this.questionMarks)))
      } catch (error) {
        console.error('Error loading tabulation data:', error)
        this.$bvToast.toast('Failed to load tabulation data', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        })
        this.records = []
        this.categories = []
        this.optionRatings = {}
        this.optionUrduRatings = {}
        this.optionLanguageMode = {}
        this.questionMarks = {}
        this.classTestMarks = {}
        this.totalRows = 0
      } finally {
        this.loading = false
      }
    },
    async loadExistingMarks() {
      if (!this.studentId) {
        console.log('loadExistingMarks: No studentId, skipping')
        return
      }

      try {
        const params = new URLSearchParams({
          db: this.$store.state.userData.db,
          cID: this.$store.state.userData.cId,
          sID: this.studentId,
        })
        
        // Only add optional parameters if they exist
        if (this.examTypeId) params.append('examTypeID', this.examTypeId)
        if (this.sessionId) params.append('sessionID', this.sessionId)
        if (this.classId) params.append('classID', this.classId)
        if (this.subjectId) params.append('subjectID', this.subjectId)
        if (this.sectionId !== null && this.sectionId !== undefined) params.append('sectionID', this.sectionId)
        if (this.type) params.append('type', this.type)

        const url = `${this.$store.state.domain}TabulationSheetMarks/GetByStudentAndFilters?${params.toString()}`
        console.log('Loading existing marks from:', url)
        
        const response = await this.get({
          url,
          token: this.$store.state.userData.token,
        })

        console.log('GetByStudentAndFilters API Response:', response)

        // Extract marks data from response
        // Handle different response structures: { data: [{ id, marksData: [...] }] } or { data: { id, marksData: [...] } }
        let marksData = null
        if (response) {
          // Check for nested data.data array structure (response.data.data)
          if (response.data && Array.isArray(response.data.data) && response.data.data.length > 0) {
            marksData = response.data.data[0]
            console.log('Extracted marksData from response.data.data[0]')
          } else if (response.data && response.data.id) {
            marksData = response.data
            console.log('Extracted marksData from response.data')
          } else if (response.data && Array.isArray(response.data) && response.data.length > 0) {
            marksData = response.data[0]
            console.log('Extracted marksData from response.data[0]')
          } else if (response.id) {
            marksData = response
            console.log('Extracted marksData from response')
          } else if (Array.isArray(response) && response.length > 0) {
            marksData = response[0]
            console.log('Extracted marksData from response[0]')
          }
        }

        console.log('Extracted marksData:', marksData)
        console.log('marksData.marksData:', marksData?.marksData)
        console.log('marksData.marksData is array?', Array.isArray(marksData?.marksData))
        console.log('marksData.marksData length:', marksData?.marksData?.length)

        if (marksData) {
          this.existingMarksData = marksData
          
          // Extract and store the marks record ID for updating existing entries
          if (marksData.id) {
            this.marksRecordId = marksData.id
            console.log('Loaded existing marks record ID:', this.marksRecordId)
          }
          
          // Clear previous detail record IDs
          this.detailRecordIds = {}
          
          // Load status if available
          if (marksData.status) {
            this.studentStatus = marksData.status
          }
          
          // Check if marksData array exists and has items
          if (marksData.marksData && Array.isArray(marksData.marksData) && marksData.marksData.length > 0) {
            // Populate marks based on type and store detail record IDs
            if (this.type === 'icon-base') {
              // Populate optionRatings and store detail record IDs
              marksData.marksData.forEach((markItem) => {
                if (markItem.optionID) {
                  const key = this.optionKey(markItem.categoryID, markItem.optionID)
                  if (markItem.rating) {
                    this.$set(this.optionRatings, key, markItem.rating)
                  }
                  if (markItem.urduRating) {
                    this.$set(this.optionUrduRatings, key, markItem.urduRating)
                  }

                  // Store detail record ID if it exists
                  if (markItem.id) {
                    this.$set(this.detailRecordIds, key, markItem.id)
                    console.log(`Stored detail record ID for ${key}:`, markItem.id)
                  }
                }
              })
            } else if (this.type === 'questions-base' || this.type === 'class-test') {
              // Populate questionMarks or classTestMarks and store detail record IDs
              console.log(`Processing ${marksData.marksData.length} mark items for type: ${this.type}`)
              
              marksData.marksData.forEach((markItem) => {
                console.log('Processing markItem:', markItem)
                
                if (markItem.questionID && markItem.obtainedMarks !== undefined) {
                  const questionId = markItem.questionID
                  const obtainedMarks = markItem.obtainedMarks
                  
                  console.log(`Setting marks for questionID ${questionId}: ${obtainedMarks}`)
                  
                  if (this.type === 'questions-base') {
                    this.$set(this.questionMarks, questionId, obtainedMarks)
                    console.log(`Set questionMarks[${questionId}] = ${obtainedMarks}`)
                  } else if (this.type === 'class-test') {
                    this.$set(this.classTestMarks, questionId, obtainedMarks)
                    console.log(`Set classTestMarks[${questionId}] = ${obtainedMarks}`)
                  }
                  
                  // Store detail record ID if it exists
                  if (markItem.id) {
                    this.$set(this.detailRecordIds, questionId, markItem.id)
                    console.log(`Stored detail record ID for questionID ${questionId}:`, markItem.id)
                  }
                } else {
                  console.warn('Mark item missing questionID or obtainedMarks:', markItem)
                }
              })
              
              console.log('Final questionMarks after loading:', JSON.parse(JSON.stringify(this.questionMarks)))
              console.log('Final classTestMarks after loading:', JSON.parse(JSON.stringify(this.classTestMarks)))
            }
            
            console.log('Detail record IDs stored:', this.detailRecordIds)
          } else {
            // marksData is empty or doesn't exist - try to fetch detail records separately
            console.log('marksData array is empty or missing. Attempting to fetch detail records...')
            
            if (this.marksRecordId) {
              // Try to fetch detail records using the marks record ID
              try {
                const detailParams = new URLSearchParams({
                  db: this.$store.state.userData.db,
                  cID: this.$store.state.userData.cId,
                  id: this.marksRecordId,
                })
                
                const detailUrl = `${this.$store.state.domain}TabulationSheetMarks/GetMarksDetails?${detailParams.toString()}`
                console.log('Fetching detail records from:', detailUrl)
                
                const detailResponse = await this.get({
                  url: detailUrl,
                  token: this.$store.state.userData.token,
                })
                
                console.log('Detail records response:', detailResponse)
                
                // Try alternative endpoint
                if (!detailResponse || (Array.isArray(detailResponse) && detailResponse.length === 0)) {
                  const altUrl = `${this.$store.state.domain}TabulationSheetMarks/GetByID?id=${this.marksRecordId}&db=${encodeURIComponent(this.$store.state.userData.db)}&cID=${this.$store.state.userData.cId}`
                  console.log('Trying alternative endpoint:', altUrl)
                  
                  const altResponse = await this.get({
                    url: altUrl,
                    token: this.$store.state.userData.token,
                  })
                  
                  console.log('Alternative endpoint response:', altResponse)
                  
                  // Extract detail records from alternative response
                  let detailRecords = []
                  if (altResponse) {
                    if (altResponse.data && Array.isArray(altResponse.data.marksData)) {
                      detailRecords = altResponse.data.marksData
                    } else if (altResponse.marksData && Array.isArray(altResponse.marksData)) {
                      detailRecords = altResponse.marksData
                    } else if (Array.isArray(altResponse.data)) {
                      detailRecords = altResponse.data
                    } else if (Array.isArray(altResponse)) {
                      detailRecords = altResponse
                    }
                  }
                  
                  if (detailRecords.length > 0) {
                    console.log(`Found ${detailRecords.length} detail records from alternative endpoint`)
                    // Process detail records same as above
                    this.processDetailRecords(detailRecords)
                  }
                } else {
                  // Process detailResponse
                  let detailRecords = []
                  if (detailResponse) {
                    if (detailResponse.data && Array.isArray(detailResponse.data)) {
                      detailRecords = detailResponse.data
                    } else if (Array.isArray(detailResponse)) {
                      detailRecords = detailResponse
                    }
                  }
                  
                  if (detailRecords.length > 0) {
                    console.log(`Found ${detailRecords.length} detail records`)
                    this.processDetailRecords(detailRecords)
                  }
                }
              } catch (detailError) {
                console.error('Error fetching detail records:', detailError)
                // Continue without detail records - marks might not be saved yet
              }
            } else {
              console.log('No marks record ID available, marks may not be saved yet')
            }
          }
        } else {
          console.log('No existing marks found for student:', this.studentId)
        }
      } catch (error) {
        console.error('Error loading existing marks:', error)
        // Don't show error toast, just log it
      }
    },
    processDetailRecords(detailRecords) {
      // Process detail records and populate marks
      if (this.type === 'icon-base') {
        detailRecords.forEach((markItem) => {
          if (markItem.optionID) {
            const key = this.optionKey(markItem.categoryID, markItem.optionID)
            if (markItem.rating) {
              this.$set(this.optionRatings, key, markItem.rating)
            }
            if (markItem.urduRating) {
              this.$set(this.optionUrduRatings, key, markItem.urduRating)
            }

            if (markItem.id) {
              this.$set(this.detailRecordIds, key, markItem.id)
            }
          }
        })
      } else if (this.type === 'questions-base' || this.type === 'class-test') {
        detailRecords.forEach((markItem) => {
          if (markItem.questionID && markItem.obtainedMarks !== undefined) {
            const questionId = markItem.questionID
            const obtainedMarks = markItem.obtainedMarks
            
            if (this.type === 'questions-base') {
              this.$set(this.questionMarks, questionId, obtainedMarks)
            } else if (this.type === 'class-test') {
              this.$set(this.classTestMarks, questionId, obtainedMarks)
            }
            
            if (markItem.id) {
              this.$set(this.detailRecordIds, questionId, markItem.id)
            }
          }
        })
        
        console.log('Processed detail records. Final questionMarks:', JSON.parse(JSON.stringify(this.questionMarks)))
        console.log('Processed detail records. Final classTestMarks:', JSON.parse(JSON.stringify(this.classTestMarks)))
      }
    },
    formatLabel(key) {
      if (!key) return ''
      return key
        .replace(/([A-Z])/g, ' $1')
        .replace(/_/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .replace(/^./, (char) => char.toUpperCase())
    },
    optionKey(categoryId, optionId) {
      return `${categoryId}-${optionId}`
    },
    showDualLanguageForCategory(category, option = null) {
      if (!this.isRemarksBaseSchool) return false
      if (category?.bothRemarks === true) return true
      if (option?.bothRemarks === true) return true
      return false
    },
    getActiveLanguage(categoryId, optionId) {
      const key = this.optionKey(categoryId, optionId)
      return this.optionLanguageMode[key] || 'E'
    },
    getRatingForOption(categoryId, optionId, lang = null) {
      const key = this.optionKey(categoryId, optionId)
      const activeLang = lang || this.getActiveLanguage(categoryId, optionId)
      return activeLang === 'U'
        ? this.optionUrduRatings[key]
        : this.optionRatings[key]
    },
    hasLanguageRating(categoryId, optionId, lang) {
      return Boolean(this.getRatingForOption(categoryId, optionId, lang))
    },
    handleLanguageSelect(categoryId, optionId, lang) {
      const key = this.optionKey(categoryId, optionId)
      this.$set(this.optionLanguageMode, key, lang)
    },
    clearLanguageRating(categoryId, optionId, lang) {
      const key = this.optionKey(categoryId, optionId)
      if (lang === 'U') {
        this.$delete(this.optionUrduRatings, key)
      } else {
        this.$delete(this.optionRatings, key)
      }
    },
    cycleLanguageRating(categoryId, optionId, lang) {
      if (this.isLocked || this.isMarksDisabled) return

      const key = this.optionKey(categoryId, optionId)
      const ratingValues = this.ratingOptions.map((r) => r.value)
      const current =
        lang === 'U' ? this.optionUrduRatings[key] : this.optionRatings[key]

      if (!current) {
        if (lang === 'U') {
          this.$set(this.optionUrduRatings, key, ratingValues[0])
        } else {
          this.$set(this.optionRatings, key, ratingValues[0])
        }
        return
      }

      const currentIndex = ratingValues.indexOf(current)
      if (currentIndex === ratingValues.length - 1) {
        this.clearLanguageRating(categoryId, optionId, lang)
      } else if (lang === 'U') {
        this.$set(this.optionUrduRatings, key, ratingValues[currentIndex + 1])
      } else {
        this.$set(this.optionRatings, key, ratingValues[currentIndex + 1])
      }
    },
    getMarkCellClass(categoryId, optionId, lang) {
      const rating = this.getRatingForOption(categoryId, optionId, lang)
      if (!rating) return 'mark-empty'
      return ['mark-rated', `mark-rated-${rating}`, `rating-${rating}`]
    },
    isRatingSelected(categoryId, optionId, ratingValue) {
      return this.getRatingForOption(categoryId, optionId) === ratingValue
    },
    setOptionRating(categoryId, optionId, ratingValue) {
      const key = this.optionKey(categoryId, optionId)
      const lang = this.getActiveLanguage(categoryId, optionId)
      if (lang === 'U') {
        this.$set(this.optionUrduRatings, key, ratingValue)
      } else {
        this.$set(this.optionRatings, key, ratingValue)
      }
    },
    selectAllRatingsForCategory(categoryId, ratingValue) {
      // Find the category
      const category = this.sortedCategories.find(cat => cat.id === categoryId)
      if (!category || !category.options || category.options.length === 0) {
        return
      }

      // Set the rating for all options in this category (English by default)
      category.options.forEach(option => {
        const key = this.optionKey(categoryId, option.id)
        this.$set(this.optionRatings, key, ratingValue)
      })
    },
    preventInvalidInput(event, maxMarks = null) {
      // Prevent minus, plus, and 'e' characters in number input
      // Allow '.' for decimal input, but prevent multiple decimal points
      if (['-', '+', 'e', 'E'].includes(event.key)) {
        event.preventDefault()
        return
      }
      
      // Allow '.' only if the input doesn't already contain a decimal point
      if (event.key === '.') {
        const currentValue = event.target.value || ''
        if (currentValue.includes('.')) {
          event.preventDefault()
          return
        }
        // Allow the decimal point
        return
      }
      
      // Limit decimal places to 2 digits
      // If typing a number after a decimal point, check if we already have 2 decimal places
      if (/^\d$/.test(event.key)) {
        const currentValue = event.target.value || ''
        if (currentValue.includes('.')) {
          const decimalPart = currentValue.split('.')[1] || ''
          // If we already have 2 decimal places, prevent typing more
          if (decimalPart.length >= 2) {
            event.preventDefault()
            return
          }
        }
      }
      
      // Allow control keys (backspace, delete, arrows, etc.)
      if (['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Tab', 'Enter', 'Home', 'End'].includes(event.key)) {
        return
      }
      
      // Allow Ctrl/Cmd combinations (copy, paste, select all, etc.)
      if (event.ctrlKey || event.metaKey) {
        return
      }
      
      // Don't block typing based on max value here - let the input handler validate
      // This allows users to type intermediate values (e.g., "18" when max is 20)
      // The handleMarksInput method will validate and correct the value
    },
    handlePaste(event, maxMarks = null) {
      // Handle paste events to limit decimal places to 2
      event.preventDefault()
      const pastedText = (event.clipboardData || window.clipboardData).getData('text')
      
      // Try to parse the pasted value
      let pastedValue = pastedText.trim()
      
      // Remove any non-numeric characters except decimal point
      pastedValue = pastedValue.replace(/[^\d.]/g, '')
      
      // Limit to 2 decimal places
      if (pastedValue.includes('.')) {
        const parts = pastedValue.split('.')
        if (parts[1]) {
          pastedValue = parts[0] + '.' + parts[1].substring(0, 2)
        }
      }
      
      // Set the value in the input field
      const numValue = Number(pastedValue)
      if (!isNaN(numValue) && numValue >= 0) {
        const roundedValue = Math.round(numValue * 100) / 100
        event.target.value = roundedValue.toString()
        // Trigger input event to update v-model
        event.target.dispatchEvent(new Event('input', { bubbles: true }))
      }
    },
    handleMarksInput(event, questionId, maxMarks) {
      if (!maxMarks || maxMarks <= 0) return
      
      // Get value directly from input event
      const inputValue = event.target.value
      
      // Allow empty input while typing - don't reset to 0 immediately
      if (inputValue === '' || inputValue === null || inputValue === undefined) {
        this.$set(this.questionMarks, questionId, 0)
        return
      }
      
      // Limit to 2 decimal places
      let processedValue = inputValue
      if (inputValue.includes('.')) {
        const parts = inputValue.split('.')
        if (parts[1] && parts[1].length > 2) {
          // Limit to 2 decimal places
          processedValue = parts[0] + '.' + parts[1].substring(0, 2)
          event.target.value = processedValue
        }
      }
      
      const marks = Number(processedValue)
      
      // Round to 2 decimal places
      const roundedMarks = Math.round(marks * 100) / 100
      
      // Validate and clamp immediately
      if (isNaN(roundedMarks)) {
        // Invalid input, reset to 0
        this.$set(this.questionMarks, questionId, 0)
        event.target.value = 0
        return
      }
      
      if (roundedMarks < 0) {
        this.$set(this.questionMarks, questionId, 0)
        event.target.value = 0
      } else if (roundedMarks > maxMarks) {
        // Immediately set to maxMarks in both v-model and input field (rounded to 2 decimals)
        const roundedMax = Math.round(maxMarks * 100) / 100
        this.$set(this.questionMarks, questionId, roundedMax)
        event.target.value = roundedMax.toFixed(2)
        
        // Show toast notification
        this.$bvToast.toast(`Marks cannot exceed ${maxMarks} for this question. Value set to ${roundedMax}.`, {
          title: 'Invalid Marks',
          variant: 'warning',
          solid: true,
          toaster: 'b-toaster-top-right',
          autoHideDelay: 3000,
        })
      } else {
        // Valid value, round to 2 decimal places and update v-model
        this.$set(this.questionMarks, questionId, roundedMarks)
        // Update input field to show proper formatting (only if it has decimals)
        if (processedValue.includes('.') && processedValue.split('.')[1].length <= 2) {
          event.target.value = processedValue
        } else if (roundedMarks % 1 !== 0) {
          event.target.value = roundedMarks.toFixed(2)
        }
      }
    },
    validateMarks(questionId, maxMarks) {
      if (!maxMarks || maxMarks <= 0) return
      
      // Get current value from v-model
      let marks = Number(this.questionMarks[questionId])
      
      if (isNaN(marks) || marks === null || marks === undefined) {
        this.$set(this.questionMarks, questionId, 0)
        return
      }
      
      // Round to 2 decimal places
      marks = Math.round(marks * 100) / 100
      
      // Immediately clamp to valid range - update synchronously
      if (marks < 0) {
        this.$set(this.questionMarks, questionId, 0)
      } else if (marks > maxMarks) {
        // Force immediate update to maxMarks (rounded to 2 decimals) - this will update the v-model
        const roundedMax = Math.round(maxMarks * 100) / 100
        this.$set(this.questionMarks, questionId, roundedMax)
        
        // Show toast notification
        this.$bvToast.toast(`Marks cannot exceed ${maxMarks} for this question. Value set to ${roundedMax}.`, {
          title: 'Invalid Marks',
          variant: 'warning',
          solid: true,
          toaster: 'b-toaster-top-right',
          autoHideDelay: 3000,
        })
      } else {
        // Ensure value is rounded to 2 decimal places
        this.$set(this.questionMarks, questionId, marks)
      }
    },
    handleClassTestMarksInput(event, testId, maxMarks) {
      if (!maxMarks || maxMarks <= 0) return
      
      // Get value directly from input event
      const inputValue = event.target.value
      
      // Allow empty input while typing - don't reset to 0 immediately
      if (inputValue === '' || inputValue === null || inputValue === undefined) {
        this.$set(this.classTestMarks, testId, 0)
        return
      }
      
      // Limit to 2 decimal places
      let processedValue = inputValue
      if (inputValue.includes('.')) {
        const parts = inputValue.split('.')
        if (parts[1] && parts[1].length > 2) {
          // Limit to 2 decimal places
          processedValue = parts[0] + '.' + parts[1].substring(0, 2)
          event.target.value = processedValue
        }
      }
      
      const marks = Number(processedValue)
      
      // Round to 2 decimal places
      const roundedMarks = Math.round(marks * 100) / 100
      
      // Validate and clamp immediately
      if (isNaN(roundedMarks)) {
        // Invalid input, reset to 0
        this.$set(this.classTestMarks, testId, 0)
        event.target.value = 0
        return
      }
      
      if (roundedMarks < 0) {
        this.$set(this.classTestMarks, testId, 0)
        event.target.value = 0
      } else if (roundedMarks > maxMarks) {
        // Immediately set to maxMarks in both v-model and input field (rounded to 2 decimals)
        const roundedMax = Math.round(maxMarks * 100) / 100
        this.$set(this.classTestMarks, testId, roundedMax)
        event.target.value = roundedMax.toFixed(2)
        
        // Show toast notification
        this.$bvToast.toast(`Marks cannot exceed ${maxMarks} for this test. Value set to ${roundedMax}.`, {
          title: 'Invalid Marks',
          variant: 'warning',
          solid: true,
          toaster: 'b-toaster-top-right',
          autoHideDelay: 3000,
        })
      } else {
        // Valid value, round to 2 decimal places and update v-model
        this.$set(this.classTestMarks, testId, roundedMarks)
        // Update input field to show proper formatting (only if it has decimals)
        if (processedValue.includes('.') && processedValue.split('.')[1].length <= 2) {
          event.target.value = processedValue
        } else if (roundedMarks % 1 !== 0) {
          event.target.value = roundedMarks.toFixed(2)
        }
      }
    },
    validateClassTestMarks(testId, maxMarks) {
      if (!maxMarks || maxMarks <= 0) return
      
      let marks = Number(this.classTestMarks[testId])
      if (isNaN(marks) || marks === null || marks === undefined) {
        this.$set(this.classTestMarks, testId, 0)
        return
      }
      
      // Round to 2 decimal places
      marks = Math.round(marks * 100) / 100
      
      // Immediately clamp to valid range
      if (marks < 0) {
        this.$set(this.classTestMarks, testId, 0)
      } else if (marks > maxMarks) {
        // Round maxMarks to 2 decimal places
        const roundedMax = Math.round(maxMarks * 100) / 100
        this.$set(this.classTestMarks, testId, roundedMax)
        this.$bvToast.toast(`Marks cannot exceed ${maxMarks} for this test. Value set to ${roundedMax}.`, {
          title: 'Invalid Marks',
          variant: 'warning',
          solid: true,
          toaster: 'b-toaster-top-right',
          autoHideDelay: 3000,
        })
      } else {
        // Ensure value is rounded to 2 decimal places
        this.$set(this.classTestMarks, testId, marks)
      }
    },
    formatMarks(value) {
      // Format marks to 2 decimal places, removing trailing zeros if it's a whole number
      if (value === null || value === undefined || isNaN(value)) return '0'
      const rounded = Math.round(value * 100) / 100
      // If it's a whole number, show without decimals, otherwise show 2 decimal places
      return rounded % 1 === 0 ? rounded.toString() : rounded.toFixed(2)
    },
    getClassTestMarksPercentage(testId, maxMarks) {
      if (!maxMarks || maxMarks === 0) return 0
      const enteredMarks = Number(this.classTestMarks[testId]) || 0
      const percentage = (enteredMarks / maxMarks) * 100
      return Math.min(100, Math.max(0, percentage))
    },
    async loadAllStudents() {
      if (!this.classId || this.sectionId === null || this.sectionId === undefined) return
      
      // If we already have students, don't reload them every time
      if (this.allStudents && this.allStudents.length > 0) return

      try {
        const dbName = encodeURIComponent(this.$store.state.userData.db)
        const cID = this.$store.state.userData.cId
        const clsID = this.classId
        const sectionID = this.sectionId || 0

        const url = `${this.$store.state.domain}students/Filterdata?cls=${clsID}&sec=${sectionID}&status=present&db=${dbName}&cId=${cID}&page=1`

        const response = await this.get({
          url,
          token: this.$store.state.userData.token,
        })

        let students = []
        if (response) {
          if (Array.isArray(response.data)) {
            students = response.data
          } else if (response.data && Array.isArray(response.data.data)) {
            students = response.data.data
          } else if (Array.isArray(response)) {
            students = response
          } else if (
            response.status === 'success' &&
            Array.isArray(response.data)
          ) {
            students = response.data
          }
        }

        this.allStudents = students.map((student) => ({
          id: student.id,
          sID: student.id,
          name: student.name || student.studentName || 'N/A',
          grNo: student.gr || student.grNo || student.registrationNumber || student.grNumber || '',
          fatherName: student.fatherName || student.father_name || student.father || '',
        }))
      } catch (error) {
        console.error('Error loading all students:', error)
        this.allStudents = []
      }
    },

    getExamTotalMarks() {
      if (this.isQuestionsBase) {
        return this.totalMarksAvailable
      }
      if (this.type === 'class-test') {
        return this.sortedClassTestRecords.reduce((sum, test) => {
          return sum + (Number(test.totalMarks || test.totalQuestion) || 0)
        }, 0)
      }
      if (this.type === 'icon-base') {
        return this.sortedCategories.reduce((sum, category) => {
          const options = (category.options || []).filter((opt) => !opt.deleted)
          return sum + options.length
        }, 0)
      }
      return 0
    },
    async saveMarks() {
      // Check if locked from API - prevent saving even if user modifies in browser
      if (this.isLocked) {
        this.$bvToast.toast('Cannot save marks. This tabulation sheet is locked.', {
          title: 'Locked',
          variant: 'warning',
          solid: true,
        })
        return
      }
      
      // Double-check by fetching latest tabulation sheet status from API
      try {
        const params = new URLSearchParams({
          db: this.$store.state.userData.db,
          cID: this.$store.state.userData.cId,
          classId: this.classId,
          sectionId: 0,
          subjectId: this.subjectId,
          type: this.type,
        })
        if (this.examTypeId) params.append('examTypeId', this.examTypeId)
        if (this.sessionId) params.append('sessionId', this.sessionId)

        const url = `${this.$store.state.domain}TabulationSheet/LoadTabulationData?${params.toString()}`
        const response = await this.get({
          url,
          token: this.$store.state.userData.token,
        })
        
        // Extract isLocked from fresh API response
        let apiIsLocked = false
        if (response && response.data) {
          if (response.data.data && response.data.data.isLocked !== undefined) {
            apiIsLocked = response.data.data.isLocked === true || response.data.data.isLocked === 1 || response.data.data.isLocked === 'true'
          } else if (response.data.isLocked !== undefined) {
            apiIsLocked = response.data.isLocked === true || response.data.isLocked === 1 || response.data.isLocked === 'true'
          }
        }
        
        if (apiIsLocked) {
          // Update local state
          this.$set(this, 'isLocked', true)
          this.$bvToast.toast('Cannot save marks. This tabulation sheet is locked.', {
            title: 'Locked',
            variant: 'warning',
            solid: true,
          })
          return
        }
      } catch (error) {
        console.error('Error checking lock status:', error)
        // Continue with save if check fails (don't block user unnecessarily)
      }
      
      if (!this.tabulationSheetId) {
        this.$bvToast.toast('Tabulation sheet ID is missing', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        })
        return
      }

      if (!this.studentId) {
        this.$bvToast.toast('Student ID is missing', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        })
        return
      }

      if (!this.studentStatus) {
        this.$bvToast.toast('Please select student status (Present/Absent/Late/N/A) before saving', {
          title: 'Status Required',
          variant: 'warning',
          solid: true,
        })
        return
      }

      this.submitting = true

      try {
        // Get userLinkID for the API
        const userLinkID = this.$store.state.userData.userLinkID || this.$store.state.userData.userID || this.$store.state.userData.localID || 0
        
        // Build marks data for current student based on type
        let currentStudentMarksData = []
        let currentStudentTotalMarks = 0

        if (this.isQuestionsBase) {
          // For questions-base: per question marks
          this.sortedQuestions.forEach((question) => {
            const marks = Number(this.questionMarks[question.id]) || 0
            currentStudentTotalMarks += marks
            
            // Build mark item with detail record ID if it exists (for updates)
            const markItem = {
              questionID: question.id,
              questionNumber: question.questionNumber || '',
              questionTotalMarks: Number(question.totalMarks) || 0,
              obtainedMarks: marks
            }
            
            // Include detail record ID if it exists (for updates)
            if (this.detailRecordIds[question.id]) {
              markItem.id = this.detailRecordIds[question.id]
              console.log(`✓ Including detail record ID ${this.detailRecordIds[question.id]} for questionID ${question.id}`)
            } else {
              console.log(`✗ No detail record ID for questionID ${question.id} (new mark)`)
            }
            
            currentStudentMarksData.push(markItem)
          })
        } else if (this.type === 'icon-base') {
          // For icon-base: per option ratings
          let ratedCount = 0
          this.sortedCategories.forEach((category) => {
            if (category.options && Array.isArray(category.options)) {
              category.options.forEach((option) => {
                const key = this.optionKey(category.id, option.id)
                const rating = this.optionRatings[key] || null
                const urduRating = this.optionUrduRatings[key] || null
                const dualLanguage = this.showDualLanguageForCategory(
                  category,
                  option
                )

                // Non dual-language: only send options that have a rating
                if (!dualLanguage && !rating) {
                  return
                }

                if (rating || urduRating) {
                  ratedCount += 1
                }

                const markItem = {
                  categoryID: category.id,
                  categoryName: category.categoryName || '',
                  categorySortIndex: category.sortIndex || 0,
                  optionID: option.id,
                  optionName: option.optionName || '',
                  optionSortIndex: option.sortIndex || 0,
                  rating: rating || null,
                  urduRating: dualLanguage ? (urduRating || null) : null,
                  urduRemarks: dualLanguage ? true : false,
                }

                if (this.detailRecordIds[key]) {
                  markItem.id = this.detailRecordIds[key]
                  console.log(
                    `✓ Including detail record ID ${this.detailRecordIds[key]} for ${key}`
                  )
                } else {
                  console.log(`✗ No detail record ID for ${key} (new rating)`)
                }

                currentStudentMarksData.push(markItem)
              })
            }
          })
          currentStudentTotalMarks = ratedCount
        } else if (this.type === 'class-test') {
          // For class-test: per test marks
          this.sortedClassTestRecords.forEach((test) => {
            const marks = Number(this.classTestMarks[test.id]) || 0
            currentStudentTotalMarks += marks
            
            // Build mark item with detail record ID if it exists (for updates)
            const markItem = {
              questionID: test.id, // Using questionID field name as per API
              questionNumber: test.name || test.testName || '',
              questionTotalMarks: Number(test.totalMarks || test.totalQuestion || 0),
              obtainedMarks: marks
            }
            
            // Include detail record ID if it exists (for updates)
            if (this.detailRecordIds[test.id]) {
              markItem.id = this.detailRecordIds[test.id]
              console.log(`✓ Including detail record ID ${this.detailRecordIds[test.id]} for testID ${test.id}`)
            } else {
              console.log(`✗ No detail record ID for testID ${test.id} (new mark)`)
            }
            
            currentStudentMarksData.push(markItem)
          })
        }

        // Build students array with ONLY the current student
        const students = [
          {
            sID: Number(this.studentId),
            status: this.studentStatus || 'P',
            marksData: currentStudentMarksData
          }
        ]

        // Absent / Leave / N/A: obtained marks are 0 but exam total must stay as max marks
        let totalMarks = currentStudentTotalMarks
        if (this.isMarksDisabled) {
          const examTotalFromMarksData = currentStudentMarksData.reduce(
            (sum, item) => sum + (Number(item.questionTotalMarks) || 0),
            0
          )
          totalMarks =
            examTotalFromMarksData ||
            this.getExamTotalMarks() ||
            Number(this.existingMarksData?.totalMarks) ||
            0
        }

        // Payload structure matching API format with all students
        let payload = {
          id: this.marksRecordId || 0, // 0 for new, >0 for update (marks record ID)
          tabulationSheetID: this.tabulationSheetId,
          students: students, // Array of all students with their marks
          tID: this.tID || null, // Teacher ID from GetByUserLinkID API
          classID: this.classId || null,
          sectionID: this.sectionId ?? 0,
          subjectID: this.subjectId || null,
          examTypeID: this.examTypeId || null,
          sessionID: this.sessionId || null,
          type: this.type,
          totalMarks: totalMarks
        }

        // Log payload for debugging
        console.log('Saving marks payload with all students:', payload)
        console.log('Marks Record ID (for update):', this.marksRecordId || 0)
        console.log('Tabulation Sheet ID:', this.tabulationSheetId)
        console.log('Detail Record IDs stored:', this.detailRecordIds)
        
        // Log detail record IDs in payload for all students
        if (payload.students && payload.students.length > 0) {
          payload.students.forEach((studentPayload) => {
            if (studentPayload.marksData && studentPayload.marksData.length > 0) {
              const hasDetailIds = studentPayload.marksData.some(m => m.id)
              const detailIdsCount = studentPayload.marksData.filter(m => m.id).length
              console.log(`Student ${studentPayload.sID}: ${detailIdsCount}/${studentPayload.marksData.length} marks have detail IDs`, {
                hasDetailIds,
                marksWithIds: studentPayload.marksData.filter(m => m.id).map(m => ({ 
                  questionID: m.questionID || m.optionID, 
                  id: m.id 
                }))
              })
            }
          })
        }

        // Call save API with userLinkID
        const response = await this.post({
          url: `${this.$store.state.domain}TabulationSheetMarks/Save?db=${encodeURIComponent(this.$store.state.userData.db)}&cID=${this.$store.state.userData.cId}&userID=${userLinkID}`,
          body: payload,
          token: this.$store.state.userData.token,
          context: this,
          message: 'Marks saved successfully!',
        })

        // Navigate to next student if available, otherwise go back to list
        if (this.hasNext && this.nextStudentId) {
          await this.navigateToNextStudent()
        } else {
          // Last student - navigate directly to marks entry list page
          this.goBackToList()
        }
        // Toast is already shown by the post action, no need to show again
      } catch (error) {
        console.error('Error saving marks:', error)
        this.$bvToast.toast('Error saving marks: ' + (error.message || 'Unknown error'), {
          title: 'Error',
          variant: 'danger',
          solid: true,
        })
      } finally {
        this.submitting = false
      }
    },
    async navigateToNextStudent() {
      if (!this.nextStudentId) return

      try {
        // Show loading state
        this.loading = true
        
        // Find next student data from allStudents
        const nextStudent = this.allStudents.find(s => s.id === this.nextStudentId)
        if (!nextStudent) {
          console.error('Next student not found')
          this.loading = false
          return
        }

        // Check if marks exist for next student
        let existingMarksData = null
        let marksRecordId = null
        
        try {
          const params = new URLSearchParams({
            db: this.$store.state.userData.db,
            cID: this.$store.state.userData.cId,
            sID: this.nextStudentId,
          })
          
          if (this.examTypeId) params.append('examTypeID', this.examTypeId)
          if (this.sessionId) params.append('sessionID', this.sessionId)
          if (this.classId) params.append('classID', this.classId)
          if (this.subjectId) params.append('subjectID', this.subjectId)
          if (this.sectionId !== null && this.sectionId !== undefined) params.append('sectionID', this.sectionId)
          if (this.type) params.append('type', this.type)

          const checkUrl = `${this.$store.state.domain}TabulationSheetMarks/GetByStudentAndFilters?${params.toString()}`
          const checkResponse = await this.get({
            url: checkUrl,
            token: this.$store.state.userData.token,
          })

          // Check if marks exist
          if (checkResponse) {
            let marksData = null
            if (checkResponse.data && checkResponse.data.id) {
              marksData = checkResponse.data
              marksRecordId = checkResponse.data.id
            } else if (checkResponse.id) {
              marksData = checkResponse
              marksRecordId = checkResponse.id
            } else if (Array.isArray(checkResponse.data) && checkResponse.data.length > 0) {
              marksData = checkResponse.data[0]
              marksRecordId = checkResponse.data[0].id
            } else if (Array.isArray(checkResponse) && checkResponse.length > 0) {
              marksData = checkResponse[0]
              marksRecordId = checkResponse[0].id
            }

            if (marksData) {
              existingMarksData = marksData
            }
          }
        } catch (error) {
          console.error('Error checking existing marks for next student:', error)
        }

        // Clear previous student's data before navigation
        this.clearStudentData()

        // Navigate to next student with updated query params
        // The watcher will automatically reload data when route changes
        await this.$router.push({
          name: "marks-entry-student",
          query: {
            studentId: this.nextStudentId,
            studentName: nextStudent.name || 'N/A',
            grNo: nextStudent.grNo || '', // Use next student's GR number
            fatherName: nextStudent.fatherName || '', // Use next student's father name
            classId: this.classId,
            className: this.className,
            subjectId: this.subjectId,
            subjectName: this.subjectName,
            sectionId: this.sectionId,
            sectionName: this.sectionName,
            examTypeId: this.examTypeId,
            examTypeName: this.examTypeName,
            sessionId: this.sessionId,
            sessionName: this.sessionName,
            type: this.type,
            typeLabel: this.typeLabel,
            marksRecordId: marksRecordId || null,
            hasExistingMarks: existingMarksData ? 'true' : 'false',
            tID: this.tID,
            studentIds: this.$route.query.studentIds, // Preserve student list
            currentIndex: (this.currentIndex + 1).toString(), // Update to next index, explicitly convert to string
          },
        })
        
        // Data will be reloaded by the watcher, but ensure loading state is managed
        // The watcher's loadStudentData will handle the loading state
      } catch (error) {
        console.error('Error navigating to next student:', error)
        this.loading = false
        this.$bvToast.toast('Error loading next student', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        })
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.student-detail-card {
  margin-bottom: 1.5rem;
  background: linear-gradient(180deg, #ffffff 0%, #faf9ff 100%);
  border: 1px solid #e0d8ff;
  border-radius: 20px;
  padding: 1.75rem;
  box-shadow: 0 10px 30px rgba(115, 103, 240, 0.12);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  animation: slideUpFade 0.4s ease-out both;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #7c5dfa 0%, #9d7aff 50%, #cbb9ff 100%);
  }

  .student-header {
    display: flex;
    align-items: center;
    gap: 1rem;
    margin-bottom: 1.5rem;
    padding-bottom: 1.25rem;
    border-bottom: 2px solid #f0edff;
  }

  .back-btn {
    width: 48px;
    height: 48px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 12px;
    border: 2px solid #e0d8ff;
    background: #fff;
    color: #7c5dfa;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    flex-shrink: 0;
    box-shadow: 0 4px 12px rgba(124, 93, 250, 0.1);

    &:hover {
      transform: translateX(-3px);
      background: linear-gradient(135deg, #7c5dfa 0%, #9d7aff 100%);
      color: #fff;
      border-color: #7c5dfa;
      box-shadow: 0 6px 20px rgba(124, 93, 250, 0.3);
    }

    &:active {
      transform: translateX(-2px);
    }
  }

  .title-wrapper {
    flex: 1;

    h4 {
      font-size: 1.5rem;
      font-weight: 800;
      color: #2d1b69;
      margin-bottom: 0.5rem;
      letter-spacing: -0.3px;
    }

    .badge-pill {
      font-weight: 600;
      letter-spacing: 0.5px;
      font-size: 0.85rem;
      padding: 0.4rem 0.9rem;
      background: linear-gradient(135deg, #e0f2f7 0%, #d1e7f0 100%);
      color: #0369a1;
      border: 1px solid #bae6fd;
      box-shadow: 0 2px 8px rgba(14, 165, 233, 0.15);
    }

    small {
      font-size: 0.95rem;
      color: #6b7280;
      font-weight: 500;
      display: flex;
      align-items: center;
      gap: 0.5rem;

      &::before {
        content: '•';
        color: #c4b5fd;
        font-weight: 700;
      }
    }
  }

  .tags {
    margin-top: 0.35rem;
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  .student-meta {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
    gap: 1rem;
    margin-top: 1rem;

    .meta-item {
      background: linear-gradient(180deg, #ffffff 0%, #f8f7ff 100%);
      border-radius: 14px;
      padding: 1rem 1.25rem;
      border: 1.5px solid #f0edff;
      box-shadow: 0 4px 12px rgba(99, 102, 241, 0.08);
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      position: relative;
      overflow: hidden;
      animation: slideUpFade 0.5s ease-out both;

      &.status-meta-item {
        background: linear-gradient(180deg, #e0f2f7 0%, #d1e7f0 100%);
        border: 2px solid #bae6fd;
        box-shadow: 0 4px 12px rgba(14, 165, 233, 0.15);

        .label {
          color: #0369a1;
          font-weight: 700;
        }
      }

      &:nth-child(1) { animation-delay: 0.05s; }
      &:nth-child(2) { animation-delay: 0.1s; }
      &:nth-child(3) { animation-delay: 0.15s; }
      &:nth-child(4) { animation-delay: 0.2s; }
      &:nth-child(5) { animation-delay: 0.25s; }
      &:nth-child(6) { animation-delay: 0.3s; }
      &:nth-child(7) { animation-delay: 0.35s; }

      &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        width: 3px;
        height: 100%;
        background: linear-gradient(180deg, #7c5dfa 0%, #cbb9ff 100%);
        transform: scaleY(0);
        transition: transform 0.3s ease;
      }

      &:hover {
        transform: translateY(-3px);
        box-shadow: 0 8px 20px rgba(99, 102, 241, 0.15);
        border-color: #d4c7ff;

        &::before {
          transform: scaleY(1);
        }
      }

      .label {
        display: block;
        font-size: 0.7rem;
        text-transform: uppercase;
        color: #8a86b7;
        letter-spacing: 1px;
        font-weight: 700;
        margin-bottom: 0.5rem;
      }

      .value {
        display: block;
        font-size: 1rem;
        font-weight: 700;
        color: #2d1b69;
        line-height: 1.3;
      }
    }
  }
}

.students-list-card {
  box-shadow: 0 6px 18px rgba(15, 23, 42, 0.06);
}

/* Due Date and Locked Badges */
.due-date-badge {
  padding: 0.625rem 1rem;
  font-size: 0.875rem;
  font-weight: 600;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: linear-gradient(135deg, #7c5dfa 0%, #9d7aff 100%) !important;
  color: #ffffff !important;
  border: none !important;
  box-shadow: 0 2px 8px rgba(124, 93, 250, 0.25);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(124, 93, 250, 0.35);
    background: linear-gradient(135deg, #6d4cdb 0%, #8b6ff5 100%) !important;
  }
  
  svg {
    color: #ffffff !important;
    width: 16px;
    height: 16px;
    stroke-width: 2.5;
  }
  
  * {
    color: #ffffff !important;
  }
}

.locked-badge {
  padding: 0.625rem 1rem;
  font-size: 0.875rem;
  font-weight: 600;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
  color: #dc2626;
  border: 2px solid #fca5a5;
  box-shadow: 0 2px 6px rgba(239, 68, 68, 0.15);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent);
    animation: shimmer 2.5s infinite;
  }
  
  svg {
    color: #dc2626;
    width: 16px;
    height: 16px;
    stroke-width: 2.5;
  }
}

@keyframes shimmer {
  0% {
    left: -100%;
  }
  100% {
    left: 100%;
  }
}

@keyframes pulse-lock {
  0%, 100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.9;
  }
}

.tabulation-card {
  background: linear-gradient(180deg, #ffffff 0%, #faf9ff 100%);
  border: 1px solid #e0d8ff;
  border-radius: 20px;
  padding: 1.75rem;
  box-shadow: 0 10px 30px rgba(115, 103, 240, 0.12);
  margin-top: 1rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #7c5dfa 0%, #9d7aff 50%, #cbb9ff 100%);
  }

  &:hover {
    box-shadow: 0 15px 40px rgba(115, 103, 240, 0.2);
    transform: translateY(-2px);
  }

  h5 {
    font-size: 1.25rem;
    font-weight: 800;
    color: #2d1b69;
    letter-spacing: -0.3px;
  }
}

/* Keyframe animations */
@keyframes slideUpFade {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}


/* Questions Base Styles */
.questions-wrapper {
  margin-top: 1rem;
}

.questions-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid #f0edff;
}

.questions-title {
  display: flex;
  align-items: center;
  font-size: 1.25rem;
  font-weight: 700;
  color: #2d1b69;
}

.questions-count {
  font-size: 0.9rem;
  font-weight: 600;
  padding: 0.5rem 1rem;
}

.questions-list {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  margin-bottom: 1.5rem;
  margin-top: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
}

.question-card {
  background: linear-gradient(180deg, #ffffff 0%, #faf9ff 100%);
  border: 1.5px solid #e0d8ff;
  border-radius: 16px;
  padding: 1.25rem;
  box-shadow: 0 6px 20px rgba(115, 103, 240, 0.12);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  animation: slideUpFade 0.4s ease-out both;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 4px;
    height: 100%;
    background: linear-gradient(180deg, #7c5dfa 0%, #cbb9ff 100%);
    transform: scaleY(0);
    transition: transform 0.3s ease;
  }

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 10px 30px rgba(115, 103, 240, 0.2);
    border-color: #b8a9ff;

    &::before {
      transform: scaleY(1);
    }
  }
}

.question-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.question-number-badge {
  width: 45px;
  height: 45px;
  border-radius: 12px;
  background: linear-gradient(135deg, #7c5dfa 0%, #9d7aff 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 6px 18px rgba(124, 93, 250, 0.4);
  transition: all 0.3s ease;
  flex-shrink: 0;

  .question-card:hover & {
    box-shadow: 0 8px 20px rgba(124, 93, 250, 0.4);
  }
}

.question-number {
  color: #fff;
  font-weight: 700;
  font-size: 1rem;
  letter-spacing: 0.5px;
}

.question-info {
  flex: 1;
}

.question-title {
  font-size: 1.1rem;
  font-weight: 700;
  color: #2d1b69;
  margin-bottom: 0.35rem;
}

.question-meta {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.marks-badge {
  font-size: 0.85rem;
  font-weight: 600;
  padding: 0.35rem 0.75rem;
}

.marks-input-section {
  margin-top: 0.75rem;
}

.marks-label {
  display: block;
  font-size: 0.85rem;
  font-weight: 600;
  color: #6b7280;
  margin-bottom: 0.5rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.marks-input-wrapper {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  background: #fff;
  border: 2px solid #e0d8ff;
  border-radius: 12px;
  padding: 0.5rem 1rem;
  transition: all 0.3s ease;

  &:focus-within {
    border-color: #7c5dfa;
    box-shadow: 0 0 0 4px rgba(124, 93, 250, 0.1);
  }
  
  &:has(input:disabled) {
    cursor: not-allowed !important;
    opacity: 0.7;
    background-color: #f3f4f6;
    border-color: #d1d5db;
    
    &:focus-within {
      border-color: #d1d5db;
      box-shadow: none;
    }
  }
}

.marks-input {
  flex: 1;
  border: none;
  outline: none;
  font-size: 1.1rem;
  font-weight: 700;
  color: #2d1b69;
  background: transparent;
  padding: 0;

  &::placeholder {
    color: #c4b5fd;
    font-weight: 500;
  }

  &:focus {
    box-shadow: none;
  }
}

.marks-slash {
  font-size: 1.2rem;
  font-weight: 600;
  color: #a78bfa;
}

.marks-total {
  font-size: 1.1rem;
  font-weight: 700;
  color: #7c5dfa;
  min-width: 40px;
  text-align: right;
}

.marks-summary-card {
  background: linear-gradient(135deg, #7c5dfa 0%, #9d7aff 100%);
  border-radius: 18px;
  padding: 1.5rem;
  box-shadow: 0 10px 30px rgba(124, 93, 250, 0.3);
  margin-bottom: 1.5rem;
  color: #fff;
  animation: slideUpFade 0.4s ease-out both;
}

.summary-header {
  display: flex;
  align-items: center;
  font-size: 1rem;
  font-weight: 700;
  margin-bottom: 1rem;
  opacity: 0.95;
}

.summary-content {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  flex-wrap: wrap;
}

.summary-item {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.summary-label {
  font-size: 0.85rem;
  font-weight: 500;
  opacity: 0.9;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.summary-value {
  font-size: 1.75rem;
  font-weight: 800;
  line-height: 1;

  &.entered {
    color: #fff;
  }

  &.total {
    color: #fef3c7;
  }
}

.summary-divider {
  width: 2px;
  height: 50px;
  background: rgba(255, 255, 255, 0.3);
  border-radius: 2px;
}

.summary-percentage {
  flex: 1;
  min-width: 200px;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.percentage-bar {
  width: 100%;
  height: 12px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 999px;
  overflow: hidden;
  position: relative;
}

.percentage-fill {
  height: 100%;
  background: linear-gradient(90deg, #fef3c7 0%, #fde68a 100%);
  border-radius: 999px;
  transition: width 0.6s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 2px 8px rgba(254, 243, 199, 0.4);
}

.percentage-text {
  font-size: 1.25rem;
  font-weight: 800;
  color: #fef3c7;
  text-align: center;
}

.categories-wrapper {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  margin-top: 1rem;
}

.category-card {
  border: 1px solid #e0d8ff;
  border-radius: 20px;
  padding: 1.5rem;
  background: linear-gradient(180deg, #ffffff 0%, #faf9ff 100%);
  box-shadow: 0 8px 30px rgba(115, 103, 240, 0.15);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  animation: slideUpFade 0.4s ease-out both;
  position: relative;
  overflow: hidden;
  width: 100%;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.4),
      transparent
    );
    transition: left 0.5s;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 30px rgba(115, 103, 240, 0.2);
    border-color: #b8a9ff;

    .icon-wrapper {
      box-shadow: 0 8px 20px rgba(124, 93, 250, 0.4);
    }
  }
}

.category-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.5rem;
}

.icon-wrapper {
  width: 52px;
  height: 52px;
  border-radius: 14px;
  background: linear-gradient(135deg, #7c5dfa 0%, #9d7aff 100%);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 20px rgba(124, 93, 250, 0.4);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 0;
    height: 0;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.3);
    transform: translate(-50%, -50%);
    transition: width 0.6s, height 0.6s;
  }

  &:active::after {
    width: 300px;
    height: 300px;
  }
}

.title-block {
  flex: 1;
  
  h6 {
    font-size: 1.1rem;
    font-weight: 700;
    letter-spacing: 0.5px;
    color: #2d1b69;
    margin-bottom: 0.15rem;
  }
  
  small {
    font-size: 0.75rem;
    color: #8a7fa8;
    font-weight: 500;
  }
}

.select-all-section {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  background: linear-gradient(135deg, #f8f7ff 0%, #f0edff 100%);
  border-radius: 12px;
  margin: 0.75rem 0;
  border: 1px solid #e0d8ff;
  flex-wrap: wrap;

  .select-all-label {
    font-size: 0.875rem;
    font-weight: 600;
    color: #5e50a3;
    white-space: nowrap;
  }

  .select-all-buttons {
    display: flex;
    gap: 0.5rem;
    flex-wrap: wrap;
  }

  .select-all-btn {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    padding: 0.4rem 0.75rem;
    border-radius: 8px;
    font-size: 0.8rem;
    font-weight: 500;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    border: 1.5px solid #7c5dfa;
    background: #fff;
    color: #7c5dfa;

    .emoji {
      font-size: 1rem;
    }

    .label {
      white-space: nowrap;
    }

    &:hover:not(:disabled) {
      background: linear-gradient(135deg, #7c5dfa 0%, #9d7aff 100%);
      color: #fff;
      border-color: #7c5dfa;
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(124, 93, 250, 0.4);
    }

    &:active:not(:disabled) {
      transform: translateY(0);
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
}

.category-divider {
  height: 2px;
  width: 100%;
  background: linear-gradient(90deg, #7c5dfa 0%, #cbb9ff 50%, #7c5dfa 100%);
  margin: 1.25rem 0;
  border-radius: 2px;
  opacity: 0.6;
}

.options-list {
  margin-top: 0.5rem;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
}

.option-row {
  background: #fff;
  border-radius: 16px;
  padding: 1rem 1.15rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 0;
  border: 1.5px solid #f0edff;
  box-shadow: 0 4px 15px rgba(99, 102, 241, 0.1);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  animation: slideUpFade 0.5s ease-out both;
  position: relative;
  overflow: hidden;
  box-sizing: border-box;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    height: 100%;
    width: 3px;
    background: linear-gradient(180deg, #7c5dfa 0%, #cbb9ff 100%);
    transform: scaleY(0);
    transition: transform 0.3s ease;
  }

  &:hover {
    transform: translateX(5px);
    box-shadow: 0 6px 20px rgba(99, 102, 241, 0.15);
    border-color: #d4c7ff;

    &::before {
      transform: scaleY(1);
    }

    .chip-index {
      box-shadow: 0 6px 18px rgba(124, 93, 250, 0.4);
      width: 38px !important;
      height: 38px !important;
    }
  }
  
  .option-label {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    width: 100%;
    min-height: 38px;
  }
}

.option-label {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  
  .chip-index {
    flex-shrink: 0;
    flex-grow: 0;
  }
  
  .chip-label {
    flex: 1;
    min-width: 0;
  }
}

.chip-index {
  width: 38px !important;
  height: 38px !important;
  min-width: 38px !important;
  min-height: 38px !important;
  max-width: 38px !important;
  max-height: 38px !important;
  border-radius: 12px;
  background: linear-gradient(135deg, #7c5dfa 0%, #9d7aff 100%);
  color: #fff;
  display: flex !important;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.95rem;
  box-shadow: 0 6px 18px rgba(124, 93, 250, 0.4);
  transition: box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  flex-shrink: 0 !important;
  flex-grow: 0 !important;
  box-sizing: border-box;

    &::after {
      content: '';
      position: absolute;
      inset: -2px;
      border-radius: 12px;
      padding: 2px;
      background: linear-gradient(135deg, #7c5dfa, #cbb9ff);
      -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
      -webkit-mask-composite: xor;
      mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
      mask-composite: exclude;
      opacity: 0;
      transition: opacity 0.3s;
    }

  &:hover::after {
    opacity: 1;
  }
}

.chip-label {
  font-weight: 600;
  color: #2f2b43;
  font-size: 0.95rem;
  letter-spacing: 0.3px;
}

// Dual-language boxed grid (myskool / myskoolprofectus)
.dual-lang-grid-scroll {
  overflow-x: auto;
  margin-bottom: 1.5rem;
  padding: 0.5rem;
  background: #fff;
}

.dual-lang-grid {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  min-width: min-content;
}

.dual-lang-column {
  flex: 1 1 0;
  min-width: 210px;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.dual-lang-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 44px 44px;
  gap: 0.35rem;
}

.dual-lang-header-row {
  .dual-lang-category-cell {
    font-weight: 700;
    text-transform: uppercase;
    font-size: 0.78rem;
    letter-spacing: 0.4px;
    background: #f8f7ff;
    color: #2d1b69;
  }

  .dual-lang-lang-head {
    font-weight: 800;
    font-size: 0.85rem;
    background: #f3f0ff;
    color: #7c5dfa;
  }
}

.dual-lang-cell {
  border: 1px solid #cfd4dc;
  border-radius: 2px;
  padding: 0.55rem 0.65rem;
  min-height: 40px;
  display: flex;
  align-items: center;
  background: #fff;
  box-sizing: border-box;
}

.dual-lang-lang-head {
  justify-content: center;
  padding: 0.35rem;
  min-height: 40px;
}

.dual-lang-text-cell {
  justify-content: flex-start;
  text-align: left;
  font-size: 0.8125rem;
  line-height: 1.35;
  color: #2f2b43;
  font-weight: 500;
}

.dual-lang-mark-cell {
  justify-content: center;
  padding: 0.35rem;
  min-height: 40px;
  min-width: 44px;
  cursor: pointer;
  position: relative;
  outline: none;
  transition: background-color 0.15s ease, border-color 0.15s ease;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.55;
  }

  &.mark-empty:hover:not(:disabled) {
    background: #f8f7ff;
    border-color: #b8b2d9;
  }

  &.mark-rated-bad {
    background: linear-gradient(135deg, #ef4444 0%, #f87171 100%);
    border-color: #dc2626;
    color: #fff;
  }

  &.mark-rated-good {
    background: linear-gradient(135deg, #10b981 0%, #34d399 100%);
    border-color: #059669;
    color: #fff;
  }

  &.mark-rated-excellent {
    background: linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%);
    border-color: #d97706;
    color: #fff;
  }

  .mark-display {
    font-weight: 800;
    font-size: 0.95rem;
    line-height: 1;
  }

  .mark-clear-x {
    position: absolute;
    top: 1px;
    right: 2px;
    font-size: 0.65rem;
    line-height: 1;
    opacity: 0.85;
    cursor: pointer;
    padding: 0 2px;

    &:hover {
      opacity: 1;
    }
  }
}

.option-lang-pills {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.option-lang-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
  min-width: 42px;
  height: 32px;
  padding: 0 0.55rem;
  border: 1.5px solid #d8d6de;
  border-radius: 8px;
  background: #fff;
  cursor: pointer;
  transition: border-color 0.15s ease, background-color 0.15s ease;
  outline: none;

  .option-lang-code {
    font-size: 0.8125rem;
    font-weight: 700;
    color: #7c5dfa;
    line-height: 1;
  }

  .lang-clear-x {
    color: inherit;
    flex-shrink: 0;
  }

  &:hover:not(:disabled):not(.active) {
    border-color: #9d7aff;
    background: #f8f7ff;
  }

  &.active {
    border-color: #7c5dfa;
    background: #7c5dfa;

    .option-lang-code,
    .lang-clear-x {
      color: #fff;
    }

    &:hover:not(:disabled) {
      border-color: #6d4ed9;
      background: #6d4ed9;
    }
  }

  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }
}

.rating-pills {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.25rem;
}

.rating-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  border-radius: 999px !important;
  padding: 0.4rem 1rem;
  font-weight: 600;
  font-size: 0.85rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  border: 2px solid transparent;
  cursor: pointer;
  white-space: nowrap;
  min-width: fit-content;
  width: auto;
  height: auto;
  
  &:disabled {
    cursor: not-allowed !important;
    opacity: 0.5;
    pointer-events: none;
  }

  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 0;
    height: 0;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.3);
    transform: translate(-50%, -50%);
    transition: width 0.4s, height 0.4s;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(124, 93, 250, 0.3);
    border-radius: 999px !important;
  }

  &:active {
    transform: translateY(0);
    border-radius: 999px !important;
  }

  .emoji {
    font-size: 1.1rem;
    transition: transform 0.3s;
    display: inline-block;
    flex-shrink: 0;
  }

  .label {
    position: relative;
    z-index: 1;
    flex-shrink: 0;
  }

  &.rating-selected {
    border-color: #7c5dfa;
    box-shadow: 0 4px 15px rgba(124, 93, 250, 0.4);
    border-radius: 999px !important;
  }

  &.rating-bad.rating-selected {
    background: linear-gradient(135deg, #ef4444 0%, #f87171 100%);
    border-color: #ef4444;
    color: white;
    border-radius: 999px !important;
  }

  &.rating-good.rating-selected {
    background: linear-gradient(135deg, #10b981 0%, #34d399 100%);
    border-color: #10b981;
    color: white;
    border-radius: 999px !important;
  }

  &.rating-excellent.rating-selected {
    background: linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%);
    border-color: #f59e0b;
    color: white;
    border-radius: 999px !important;
  }
}

.sort-selector ::v-deep .form-control {
  min-width: 180px;
}

.students-table ::v-deep td {
  vertical-align: middle;
}

.action-pill {
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
}

/* Class Test Records Styles */
.class-test-wrapper {
  margin-top: 1.5rem;
}

.class-test-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 1.25rem;
  margin-top: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
}

.class-test-card {
  background: linear-gradient(180deg, #ffffff 0%, #faf9ff 100%);
  border: 2px solid #e0d8ff;
  border-radius: 16px;
  padding: 1.5rem;
  box-shadow: 0 4px 16px rgba(115, 103, 240, 0.1);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  animation: slideUpFade 0.4s ease-out both;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #7c5dfa 0%, #9d7aff 50%, #cbb9ff 100%);
    background-size: 200% 100%;
    animation: shimmer 3s infinite;
  }

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(115, 103, 240, 0.2);
    border-color: #b8a9ff;
  }
}

.class-test-header {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 1.25rem;
}

.test-number-badge {
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: linear-gradient(135deg, #7c5dfa 0%, #9d7aff 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(124, 93, 250, 0.3);
  position: relative;
  overflow: hidden;

    &::after {
      content: '';
      position: absolute;
      top: -50%;
      left: -50%;
      width: 200%;
      height: 200%;
      background: linear-gradient(45deg, transparent, rgba(255, 255, 255, 0.3), transparent);
      transform: rotate(45deg);
    }
}

.test-number {
  font-size: 1.25rem;
  font-weight: 800;
  color: #ffffff;
  position: relative;
  z-index: 1;
}

.test-title-section {
  flex: 1;
  min-width: 0;
}

.test-name {
  font-size: 1.1rem;
  font-weight: 700;
  color: #2d1b69;
  margin-bottom: 0.5rem;
  line-height: 1.4;
  letter-spacing: -0.2px;
}

.test-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  align-items: center;
}

.test-meta-item {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.85rem;
  color: #6b7280;
  font-weight: 500;
  background: #f3f4f6;
  padding: 0.25rem 0.75rem;
  border-radius: 8px;

  svg {
    width: 14px;
    height: 14px;
    stroke-width: 2;
  }
}

.class-test-content {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 2px solid #f0edff;
}

.marks-display {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.class-test-card {
  .marks-input-section {
    margin-top: 0;
  }

  .marks-label {
    display: block;
    font-size: 0.85rem;
    font-weight: 600;
    color: #6b7280;
    margin-bottom: 0.5rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .marks-input-wrapper {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    background: #fff;
    border: 2px solid #e0d8ff;
    border-radius: 12px;
    padding: 0.5rem 1rem;
    transition: all 0.3s ease;

    &:focus-within {
      border-color: #7c5dfa;
      box-shadow: 0 0 0 3px rgba(124, 93, 250, 0.1);
    }
    
    &:has(input:disabled) {
      cursor: not-allowed !important;
      opacity: 0.7;
      background-color: #f3f4f6;
      border-color: #d1d5db;
      
      &:focus-within {
        border-color: #d1d5db;
        box-shadow: none;
      }
    }
  }

  .marks-input {
    flex: 1;
    border: none;
    outline: none;
    font-size: 1.1rem;
    font-weight: 700;
    color: #2d1b69;
    background: transparent;
    padding: 0;

    &::placeholder {
      color: #9ca3af;
      font-weight: 400;
    }
    
    &:disabled {
      cursor: not-allowed !important;
      opacity: 0.6;
      color: #9ca3af;
      background-color: #f3f4f6;
    }
  }

  .marks-slash {
    font-size: 1.25rem;
    font-weight: 600;
    color: #9ca3af;
  }

  .marks-total {
    font-size: 1.25rem;
    font-weight: 700;
    color: #374151;
    min-width: 40px;
    text-align: right;
  }
}

.marks-progress {
  width: 100%;
}

.progress-bar {
  width: 100%;
  height: 8px;
  background: #e5e7eb;
  border-radius: 999px;
  overflow: hidden;
  position: relative;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #7c5dfa 0%, #9d7aff 100%);
  border-radius: 999px;
  transition: width 0.6s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 2px 8px rgba(124, 93, 250, 0.3);
}

/* Empty State for Class Test */
.empty-state-box {
  padding: 4rem 2rem;
  text-align: center;
  background: linear-gradient(180deg, #fafafa 0%, #ffffff 100%);
  border-radius: 16px;
  border: 2px dashed #e5e7eb;
  margin: 2rem 0;
  animation: slideUpFade 0.5s ease-out both;
}

.empty-state-content {
  max-width: 500px;
  margin: 0 auto;
}

.empty-state-icon {
  margin-bottom: 1.5rem;
  color: #9ca3af;
  
  svg {
    stroke-width: 1.5;
  }
}

.empty-state-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 0.75rem;
  letter-spacing: -0.3px;
}

.empty-state-message {
  font-size: 1rem;
  color: #6b7280;
  font-weight: 500;
  margin-bottom: 0.5rem;
  line-height: 1.6;
}

.empty-state-submessage {
  font-size: 0.875rem;
  color: #9ca3af;
  font-weight: 400;
  line-height: 1.5;
}

.records-badge {
  font-size: 0.9rem;
  font-weight: 700;
  padding: 0.5rem 1rem;
  background: linear-gradient(135deg, #e0f2f7 0%, #d1e7f0 100%);
  color: #0369a1;
  border: 1px solid #bae6fd;
  box-shadow: 0 2px 8px rgba(14, 165, 233, 0.15);
}

.save-marks-btn {
  font-weight: 600;
  border-radius: 999px;
  padding: 0.5rem 1.25rem;
  box-shadow: 0 2px 8px rgba(40, 199, 111, 0.3);
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
  border: none;
  color: white;
  font-size: 0.875rem;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(40, 199, 111, 0.4);
    background: linear-gradient(135deg, #218838 0%, #1ea080 100%);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    background: #6c757d;
  }

  .feather-icon {
    margin-right: 0.5rem;
  }
}

.gap-2 {
  gap: 0.5rem;
}

.student-progress-badge {
  font-size: 0.875rem;
  font-weight: 700;
  padding: 0.5rem 1rem;
  background: linear-gradient(135deg, #7c5dfa 0%, #9d7aff 100%) !important;
  color: #ffffff !important;
  border: none;
  box-shadow: 0 4px 16px rgba(124, 93, 250, 0.35);
  border-radius: 999px;
  letter-spacing: 0.3px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: left 0.5s;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(124, 93, 250, 0.45);
    
    &::before {
      left: 100%;
    }
  }
}

::v-deep .student-progress-badge {
  color: #ffffff !important;
  background: linear-gradient(135deg, #7c5dfa 0%, #9d7aff 100%) !important;
}

.status-section {
  background: linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%);
  border: 2px solid #e0d8ff;
  border-radius: 16px;
  padding: 1.25rem 1.5rem;
  box-shadow: 0 4px 16px rgba(115, 103, 240, 0.1);
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, #7c5dfa 0%, #9d7aff 50%, #cbb9ff 100%);
  }

  &:hover {
    box-shadow: 0 6px 20px rgba(115, 103, 240, 0.15);
    border-color: #d4c7ff;
  }

  .status-section-content {
    display: flex;
    gap: 1.5rem;
    align-items: flex-start;
    flex-wrap: nowrap;
    width: 100%;

    @media (max-width: 992px) {
      flex-direction: column;
      gap: 1.5rem;
      flex-wrap: wrap;
    }
  }

  .status-left {
    flex: 0 0 auto;
    min-width: 280px;
    max-width: 350px;

    @media (max-width: 992px) {
      width: 100%;
      max-width: 100%;
    }
  }

  .status-right {
    flex: 1 1 auto;
    min-width: 0;
    width: 100%;
  }

  .status-label {
    color: #2d1b69;
    font-size: 1rem;
    font-weight: 700;
    letter-spacing: 0.3px;
  }

  .status-buttons {
    display: flex;
    gap: 0.75rem;
    flex-wrap: wrap;
  }

  .status-btn {
    width: 50px;
    height: 50px;
    min-width: 50px;
    font-weight: 700;
    font-size: 1rem;
    border-radius: 50%;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    border: 2px solid;
    background: white !important;
    padding: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    position: relative;
    overflow: hidden;
    color: inherit !important;
    text-align: center;
    line-height: 1;
    z-index: 1;
    cursor: pointer;
    
    &:disabled {
      cursor: not-allowed !important;
      opacity: 0.5;
      pointer-events: none;
    }

    // P (Present) - Green
    &.status-btn-p {
      border-color: #10b981;
      color: #10b981 !important;
      background: white !important;

      &.status-btn-active {
        background: linear-gradient(135deg, #10b981 0%, #34d399 100%) !important;
        border-color: transparent;
        border: none;
        color: white !important;
        box-shadow: 0 4px 20px rgba(16, 185, 129, 0.4);
        transform: translateY(-2px);

        &:hover {
          transform: translateY(-3px);
          box-shadow: 0 6px 24px rgba(16, 185, 129, 0.5);
          background: linear-gradient(135deg, #059669 0%, #10b981 100%) !important;
        }
      }

      &:hover:not(.status-btn-active) {
        background: white !important;
        border-color: #34d399;
        color: #059669 !important;
        transform: translateY(-2px);
        box-shadow: 0 4px 16px rgba(16, 185, 129, 0.2);
      }
    }

    // A (Absent) - Red
    &.status-btn-a {
      border: 2px solid #ef4444 !important;
      color: #ef4444 !important;
      background: white !important;

      &.status-btn-active {
        background: linear-gradient(135deg, #ef4444 0%, #f87171 100%) !important;
        border-color: transparent;
        border: none;
        color: white !important;
        box-shadow: 0 4px 20px rgba(239, 68, 68, 0.4);
        transform: translateY(-2px);

        &:hover {
          transform: translateY(-3px);
          box-shadow: 0 6px 24px rgba(239, 68, 68, 0.5);
          background: linear-gradient(135deg, #dc2626 0%, #ef4444 100%) !important;
        }
      }

      &:hover:not(.status-btn-active) {
        background: white !important;
        border-color: #f87171;
        color: #dc2626 !important;
        transform: translateY(-2px);
        box-shadow: 0 4px 16px rgba(239, 68, 68, 0.2);
      }
    }

    // L (Late) - Blue
    &.status-btn-l {
      border: 2px solid #3b82f6 !important;
      color: #3b82f6 !important;
      background: white !important;

      &.status-btn-active {
        background: linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%) !important;
        border-color: transparent;
        border: none;
        color: white !important;
        box-shadow: 0 4px 20px rgba(59, 130, 246, 0.4);
        transform: translateY(-2px);

        &:hover {
          transform: translateY(-3px);
          box-shadow: 0 6px 24px rgba(59, 130, 246, 0.5);
          background: linear-gradient(135deg, #2563eb 0%, #3b82f6 100%) !important;
        }
      }

      &:hover:not(.status-btn-active) {
        background: white !important;
        border-color: #60a5fa;
        color: #2563eb !important;
        transform: translateY(-2px);
        box-shadow: 0 4px 16px rgba(59, 130, 246, 0.2);
      }
    }

    // N/A - Grey
    &.status-btn-n-a {
      border: 2px solid #6b7280 !important;
      color: #6b7280 !important;
      background: white !important;

      &.status-btn-active {
        background: linear-gradient(135deg, #6b7280 0%, #9ca3af 100%) !important;
        border-color: transparent;
        border: none;
        color: white !important;
        box-shadow: 0 4px 20px rgba(107, 114, 128, 0.4);
        transform: translateY(-2px);

        &:hover {
          transform: translateY(-3px);
          box-shadow: 0 6px 24px rgba(107, 114, 128, 0.5);
          background: linear-gradient(135deg, #4b5563 0%, #6b7280 100%) !important;
        }
      }

      &:hover:not(.status-btn-active) {
        background: white !important;
        border-color: #9ca3af;
        color: #4b5563 !important;
        transform: translateY(-2px);
        box-shadow: 0 4px 16px rgba(107, 114, 128, 0.2);
      }
    }

    &:active {
      transform: translateY(0);
    }
  }
}

/* Icon Reference Section */
.icon-reference-wrapper {
  border-left: 2px solid #e0d8ff;
  padding-left: 2rem;
  flex: 1;
  min-width: 0;

  @media (max-width: 992px) {
    border-left: none;
    border-top: 2px solid #e0d8ff;
    padding-left: 0;
    padding-top: 1.5rem;
    width: 100%;
  }
}

.icon-reference-list {
  display: flex;
  flex-direction: row;
  gap: 0.5rem;
  flex-wrap: nowrap;
  align-items: stretch;
  width: 100%;
}

.icon-reference-item {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.6rem 0.8rem;
  background: #ffffff;
  border-radius: 8px;
  border: 1.5px solid #f0edff;
  transition: all 0.3s ease;
  flex: 1 1 0;
  min-width: 0;
  max-width: 100%;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(115, 103, 240, 0.15);
    border-color: #d4c7ff;
  }

  @media (max-width: 1200px) {
    padding: 0.5rem 0.6rem;
    gap: 0.5rem;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    text-align: center;
    padding: 0.75rem;
  }
}

.reference-icon {
  width: 40px;
  height: 40px;
  min-width: 40px;
  object-fit: contain;
  border-radius: 6px;
  background: #faf9ff;
  padding: 0.2rem;
  transition: transform 0.3s ease;
  flex-shrink: 0;

  @media (max-width: 1200px) {
    width: 35px;
    height: 35px;
    min-width: 35px;
  }
}

.icon-reference-item:hover .reference-icon {
  transform: scale(1.05);
}

.reference-text {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  flex: 1;
  min-width: 0;
  overflow: hidden;
}

.reference-label {
  font-size: 0.85rem;
  font-weight: 700;
  color: #2d1b69;
  letter-spacing: 0.2px;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (max-width: 1200px) {
    font-size: 0.8rem;
  }
}

.reference-description {
  font-size: 0.7rem;
  color: #6b7280;
  font-weight: 500;
  line-height: 1.25;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (max-width: 1200px) {
    font-size: 0.65rem;
    -webkit-line-clamp: 2;
    line-clamp: 2;
  }
}
</style>

